import * as utils from './utils.js';
//name specific functions from utils to use without utils. prefix
const { log, getBootstrap, getTemplate, nextFrame, delay, displayLongDistance, displayShortDistance, round0, createTranslator, childRouteNameLookup } = utils;

import { getAllPlaceData, getReversedPlaceData, getPlaceById, fetchAPIData, getPlaceBySlug, getPlaceIdBySlug, getRouteById, getRouteIdBySlug, getPlaceIndexAlongRoute, getPlaceAlongRoute, getRouteBySlug, validatePathSlugs, getFetchedAPIData, getChildRoutePlaceIsAlong, getAllowedMarkers, getChildRoutes, getNextPlaceIdAlongRoute, getPathQueryString, calculatePathData, getRouteIdBySlug_appConfigLookup, showRouteSelectModal, showOriginSelectModal, showDestinationSelectModal, openPaceSliderPopup, destroyRangeSliders} from './mapPlannerShared.js';
import { pathfinder } from './pathfinder.js';
import { doMapboxFreeCameraAnimation } from './mapRoutePreview.js';
import { setupRouteProgress } from './mapRouteProgress.js';
// import { getFalconWaldo, setFalconWaldo, setFalconModalContent, openFalconModal } from './falconModal.js';
import { setFenixModalContent, openFenixModal, closeFenixModal, getFenixModalElement, setFenixBackdrop, getFenixModalIsOpen } from './fenixModal.js';
import { getRoutePlaces, addChildRoutePlaces, sliceRoutePlacesFromOrigin, sliceRoutePlacesToDestination, filterRoutePlacesByIndex, renderFilteredPlaceList, setGlobalPathStops, getGlobalPathStops, clearGlobalPathStops, filterMasterServicesList, addCalculatedValuesFromOrigin } from './placeList.js';
import { initSwipersIn, getMapPopupSwiper_swiperInstance, nullMapPopupSwiper_swiperInstance } from './hulk.js';

const $ = window.jQuery;
const t = createTranslator(['map','mapPlannerShared']);

//Define global variables
export let mapInstance; // Mapbox instance
export let mapboxgl; // Mapbox GL instance
let pathSegments; // Globalize path segments, so that they persist between calls, and can be reused in case only map markers change and route is the same.
let onPathLineString = null; // Global var to hold the onPath line string for use by elevation chart and route preview.
const defaultMapStyle = 'outdoors';
let startingMapStyle = defaultMapStyle;
const startingMapCenter = [-4.411852, 42.421373];
const startingMapZoom = 6;
const defaultMap3dBearing = 160; // Default bearing for 3D map.
let map3dSettings = {
    pitch: 76,
    center: null, // null means use current map center.
    bearing: defaultMap3dBearing,
    zoom:14,
    speed: 1.2
};
let mapRouteProgress = null; // Global var to hold the map route progress instance.
let chartJsLoaded = false;
// let turfJsLoaded = false;
let elevationChartOffcanvas = null;
let elevationChartInstance = null;
let elevationChartHoverX = null;
let elevationMapMarker = null;

let previousState = {}; // Previous state to compare with current state. Set default style to match the style in the URL so it doesn't trigger a style change on page load.

// Save map markers using WP Post id as the key.
// NOTE: Object keys are always converted to strings.
let mapMarkers = {};
let santiagoMapMarker = null;
let friendMarkers = {};
let originMapMarkerPlaceId = null;
let destinationMapMarkerPlaceId = null;

let selectedMapMarker = null; // Global var to hold the selected map marker. { el, type, id, lngLat, routeId }
let openMapMarkerPopup = null; // Global var to hold the open map marker popup object.
const desktopMediaQuery = utils.desktopMediaQuery();

let onPageLoadOpenMarkerId = null; // Global var to hold the marker id to open on page load.

/**
 * Initializes the map page.
 */
export function initMapPage() {

    // Get starting map style from URL
    const startingParams = new URLSearchParams(window.location.search);

    // If set, save value of ?marker_id to onPageLoadOpenMarkerId before it gets cleared by the parsing.
    if(startingParams.get('show_marker_id')) {
        log('onPageLoadOpenMarkerId set to:', parseInt(startingParams.get('show_marker_id')));
        onPageLoadOpenMarkerId = parseInt(startingParams.get('show_marker_id'));
    }
    
    if(startingParams.get('style') && getMapStyle(startingParams.get('style'), true) && window.ironman.userData.permissions.allowedMapStyles.includes(startingParams.get('style'))) {
        startingMapStyle = startingParams.get('style');
    }
    // Init previous state to match starting map style.
    previousState.style = startingMapStyle;

    // Prefetch API data. Starts downloading friends, mapserver, and/or listings data ASAP based on URL state.
    prefetchAPIData();
   
    // Load the Mapbox Map instance
    const mapPromise = initMap();

    // Fetch Place API data upon pageload. Either from cache or by downloading it.
    const placeDataPromise = fetchAPIData('places', 'all', null, 'page-blocking', t('downloadingPlacesDataSpinnerMessage')).then(function() {
        return getFetchedAPIData('places').then(function() {
            // Do nothing. placeData now ready to use.
            // FYI: If I wanted to use the place data here, I could add a var inside the function() and make sure getFetchedAPIData() returns it.

        });
    });

    // Load Map Page UI and click handlers
    initMapPageUI();

    // Wait for map and place data to load before updating the map based on the current URL state
    // note: Promise.all returns a promise that resolves when all promises have resolved
    // note: If I want to get the returns from either promise, I can add [mapResult, placeData] inside the .then(function(mapResult, placeData)).
    Promise.all([mapPromise, placeDataPromise]).then(function() {        
        log('Map and place data loaded successfully.');
        updateMapFromUrl();

        // Add event listener for URL changes
        window.addEventListener('popstate', function() {    
            log('Popstate event detected, updating map from URL state.');
            updateMapFromUrl();
        });
    }).catch(function(error) {
        log('Error loading map page:', error);
    });
}

/**
 * Prefetches API data based on the URL state.
 * Prefetch mapserver, friends, and/or listings data.
 */
function prefetchAPIData() {
    const startingParams = new URLSearchParams(window.location.search);

    // Validate all input markers values into startingMarkers array of slugs.
    const allowedMarkers = getAllowedMarkers();
    let allAllowedMarkers = [];
    //Flatten allowedMarkers into a single array of slugs.
    for(const [markerType, markerSlugs] of Object.entries(allowedMarkers)) {
        allAllowedMarkers.push(markerType);
        allAllowedMarkers.push(...markerSlugs);
    }
    // log('Allowed markers:', allAllowedMarkers);

    let startingMarkers = [];
    // Split the markers string into an array of slugs.
    if(startingParams.get('markers')) {
        const inputStartingMarkers = startingParams.get('markers').split(',');
        for(const markerSlug of inputStartingMarkers) {
            // Validate the marker slug. Include all valid marker types.
            if(allAllowedMarkers.includes(markerSlug)) {
                startingMarkers.push(markerSlug);
            }
        }
    }
    
    log('Starting markers:', startingMarkers);

    // Check the marker filter buttons to match the startingMarkers array.
    let showServiceListviewButton = false;
    $('input[name="marker-filters"]').each(function() {
        if(startingMarkers.includes($(this).val())) {
            $(this).prop('checked', true);
            showServiceListviewButton = true;
        }
    });

    if(showServiceListviewButton) {
        // TODO: Listview.
        // $('[data-label="show-service-listview-button"]').removeClass('d-none');
    }

    // If startingMarkers contains "friends", start prefetching friends API data.
    if(startingMarkers.includes('friends')) {
        fetchAPIData('friends', 'all', null, 'non-blocking', t('gettingFriendsDataSpinnerMessage'));
        log('Prefetching friends API data.');
    }

    // Remove friends, places, and place types from startingMarkers.
    startingMarkers = startingMarkers.filter(marker => !['friends', 'places', 'city', 'town', 'village', 'landmark', 'rest-area'].includes(marker));

    // Validate and start prefetching the map server data for the route specified in the URL.
    // WARNING: placeData NOT yet available here. Validate route with global app data.
    // NOTE: Fetch Mapserver data by slug.
    if(startingParams.get('route') && window.ironman.appConfig.primaryRoutes) {
        // Make sure route exists in primaryRoutes lookup.
        if(getRouteIdBySlug_appConfigLookup(startingParams.get('route'))) {
            fetchAPIData('mapserver', startingParams.get('route'), null, 'page-blocking', t('downloadingRouteMapDataSpinnerMessage'));
            log('Prefetching mapserver API data for route:', startingParams.get('route'));

            log('Starting markers:', startingMarkers);
            if(startingMarkers.length > 0) {
                // Start prefetching the listings data for the route.
                // Maybe switch to page blocking bcz its on page load?
                fetchAPIData('listings', startingParams.get('route'), null, 'non-blocking', t('downloadingListingsDataSpinnerMessage'));
                log('Prefetching listings API data for route:', startingParams.get('route'));
            }

            // Prefetching is done, end function.
            return;
        }
    }

    // If we didn't return earlier, it's because no route was provided, or it wasn't a valid primary route, so fetch all routes in low-res.
    fetchAPIData('mapserver', 'all', null, 'page-blocking', t('downloadingRoutesMapDataSpinnerMessage'));
    log('Prefetching all routes in low-res.');
}

/**
 * Initializes the Mapbox map.
 */
function initMap() {
    return import('mapbox-gl').then((mapboxgl_module) => {
        
        // Define constants for mapbox-gl
        mapboxgl = mapboxgl_module.default;
        mapboxgl.accessToken = window.ironman.appConfig.mapboxAccessToken;
        
        // Create the map
        mapInstance = new mapboxgl.Map({
            container: 'map',
            style: getMapStyle(startingMapStyle),
            projection: 'mercator',   // force flat Mercator at all zooms. By default, the map transitions to a sphere at higher zooms, and the markers drift strangely.
            center: startingMapCenter, // starting position
            zoom: startingMapZoom, // starting zoom
        });

        // Add zoom and rotation controls to the map.
        mapInstance.addControl(new mapboxgl.NavigationControl(), 'bottom-right');

        // Add geolocate control to the map.
        const geolocateControl = new mapboxgl.GeolocateControl({
            positionOptions: {
                enableHighAccuracy: true
            },
            // When active the map will receive updates to the device's location as it changes.
            trackUserLocation: true,
            // Draw an arrow next to the location dot to indicate which direction the device is heading.
            showUserHeading: true
        });
        mapInstance.addControl(geolocateControl, 'bottom-right'); // Move down to position AFTER custom controls.

        // Setup route progress.
        mapRouteProgress = setupRouteProgress({
            map: mapInstance,
            geolocateControl: geolocateControl,
            maxDistanceKm: 20000,
            updateIntervalMs: 5000,
            showRouteProgress: function() {
                log('Showing route progress.');
            },
            hideRouteProgress: function() {
                log('Hiding route progress.');
            },
            updateRouteProgress: function(percent, closestPoint) {
                log('Updating route progress:', percent, closestPoint);
            }
        });

        mapInstance.on('style.load', function() {
            log('Map style loaded.');

            // Disable spinner.
            utils.updateLoadingSpinner('settingMapStyle', false, 'non-blocking');

            // Create EMPTY map sources and layers to fill in later with map data.
            createMapLinesSourcesAndLayers();
            
        });

        // Add custom buttons controls to the map.
        mapInstance.on("load", function () {
            mapInstance.addControl(
                createCustomButtonsControl({
                    on3D: function (map) {
                        // your 3D logic here
                        console.log("3D toggle");
                        toggle3dMap();
                    },
                    onStyle: function (map) {
                        // your style toggle logic here
                        console.log("Style toggle");
                        toggleMapStyleSelectModal();
                    }
                }),
                "bottom-right"
            );
            // mapInstance.addControl(geolocateControl, 'top-right'); // If wanted to add it after custom controls.
        });

        // Add event listener for map container clicks.
        mapInstance.on("load", function () {
            const mapContainer = mapInstance.getContainer();
            mapContainer.addEventListener("click", onMapContainerClick);
        });

        // FOR DEBUGGING: Add event listener for zoom level changes.
        // mapInstance.on('zoom', () => {
        //     console.log('Zoom level:', mapInstance.getZoom());
        // });

        // Return a promise that resolves when the map is loaded
        return new Promise(function (resolve) {
            mapInstance.on('load', function() {
                log('Map loaded successfully.');
                resolve(); // Resolve the promise

                // Add event listeners for map interactions
                // mapInstance.on('click', function(e) {
                //     log('Map clicked at:', e.lngLat);
                //     // Handle map click events, e.g. show popup or marker
                // });

                // Add other event listeners as needed...
            });
        });

    }).catch((err) => {
        console.error('Failed to load Mapbox:', err);
        throw err;
    });
}

/**
 * Creates a custom buttons control for the map.
 * @param {Object} options - The options for the control.
 * @returns {Object} - The control object.
 */
function createCustomButtonsControl(options) {
    var map;
    var container;
  
    return {
      onAdd: function (m) {
            map = m;
    
            container = document.createElement("div");
            container.className = "mapboxgl-ctrl mapboxgl-ctrl-group";
    
            // --- 3D button ---
            var btn3d = document.createElement("button");
            btn3d.type = "button";
            btn3d.className = "mapboxgl-ctrl-icon mbx-custom-icon-3d";
            btn3d.setAttribute("aria-label", "Toggle 3D");
            btn3d.onclick = function () {
                if (options.on3D) {
                    options.on3D(map);
                }
            };
    
            // --- Style button ---
            var btnStyle = document.createElement("button");
            btnStyle.type = "button";
            btnStyle.className = "mapboxgl-ctrl-icon mbx-custom-icon-style";
            btnStyle.setAttribute("aria-label", "Change map style");
            btnStyle.onclick = function () {
                if (options.onStyle) {
                    options.onStyle(map);
                }
            };
    
            // Add the buttons to the container in order.
            container.appendChild(btnStyle);
            container.appendChild(btn3d);
    
            return container;
      },
  
      onRemove: function () {
        container.parentNode.removeChild(container);
        map = null;
      }
    };
}
  

/**
 * Creates a source and layers for the map lines.
 * This function checks if the source already exists to avoid duplication.
 * It creates a GeoJSON source for the segments and two layers:
 * 1. `chosenHighlight` - for highlighting chosen segments.
 * 2. `segmentsLayer` - for displaying all segments with their respective colors and widths.
 * @returns {void}
 * */
function createMapLinesSourcesAndLayers() {
    log('Creating map lines sources and layers...');
    // If the source already exists, bail out
    if (mapInstance.getSource('segmentsSource')) {
        log('Map lines sources and layers already exist, skipping creation.');
        return;
    }
  
    // Add a source for the segments.
    mapInstance.addSource('segmentsSource', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        // lineMetrics: true
    });

    // Add a source for the route background.
    mapInstance.addSource('routeBackgroundSource', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
    });

    // Add a layer for the route background.
    mapInstance.addLayer({
        id: 'routeBackgroundLayer',
        type: 'line',
        source: 'routeBackgroundSource',
        paint: {
            // 'line-color': ['get', 'color'],
            'line-color': '#222222',
            // 'line-width': 30,
            'line-width': [
                'interpolate',
                ['linear'],
                ['zoom'],
                5, 5,   // zoom level 5 → 0.5px
                15, 12     // zoom level 15 → 6px
            ],
            'line-opacity': 0.3,
            // 'line-blur': 10,
            'line-translate': [2,2]
        }
    });

    // Add a layer for the onPath segments.
    mapInstance.addLayer({
        id: 'onPathLayer',
        type: 'line',
        source: 'segmentsSource',
        filter: ['==', ['get', 'onPath'], true],
        paint: {
            'line-color': 'white',
            // 'line-width': 12,
            'line-width': [
                'interpolate',
                ['linear'],
                ['zoom'],
                5, 9,   // zoom level 5 → 0.5px
                // 10, 10,    // zoom level 10 → 2px
                15, 12     // zoom level 15 → 6px
            ],
        },
        layout: {
            'line-cap': 'round',
            'line-join': 'round'
        }
    });
  
    // Add a layer for the foreground segments.
    mapInstance.addLayer({
        id: 'segmentsLayer',
        type: 'line',
        source: 'segmentsSource',
        paint: {
            // 'line-width': ['get', 'line_width'],
            // 'line-width': 6,
            'line-width': [
                'interpolate',
                ['linear'],
                ['zoom'],
                5, 4.5,   // zoom level 5 → 0.5px
                // 10, 5,    // zoom level 10 → 2px
                15, 6     // zoom level 15 → 6px
            ],
            'line-opacity': ['get', 'opacity'],
            // 'line-opacity': 1,
            'line-color': ['get', 'color'],
            'line-dasharray': [
                'step',
                ['zoom'],

                // country level / very zoomed out
                [
                    'case',
                    ['boolean', ['get', 'isDashed'], false],
                    ['literal', [0.5, 2.5]],
                    ['literal', [1, 0]]
                ],

                7,
                [
                    'case',
                    ['boolean', ['get', 'isDashed'], false],
                    ['literal', [0.75, 2]],
                    ['literal', [1, 0]]
                ],

                9,
                [
                    'case',
                    ['boolean', ['get', 'isDashed'], false],
                    ['literal', [1, 2]],
                    ['literal', [1, 0]]
                ],

                12,
                [
                    'case',
                    ['boolean', ['get', 'isDashed'], false],
                    ['literal', [1.5, 2.5]],
                    ['literal', [1, 0]]
                ]
            ]
            // 'line-color': '#fa9f0d', //Gold standard color.
            //Line-gradient requires lineMetrics: true in the source.
            // 'line-gradient': [
            //     'interpolate',
            //     ['linear'],
            //     ['line-progress'],
            //     0, '#ffcc2d',
            //     1, '#fa9f0d'
            // ]
        },
        layout: {
            'line-cap': 'round',
            'line-join': 'round'
        }
    });
}

/**
 * Initializes the Map Page UI and click handlers.
 */
function initMapPageUI() {
    // Add event listener for desktop media query changes.
    desktopMediaQuery.addEventListener('change', onBreakpointChange);

    // Put all Map Page click handlers here...

    $(document).on('click', '[data-action="openSelectRoutePopup"]', function (event) {
        event.preventDefault();
        log('openSelectRoutePopup clicked');

        // Get already selected values
        const state = getStateFromUrl();
        const selectedRouteSlug = state.route || null;
        const selectedDirection = state.direction || null;

        // Open Popup
        showRouteSelectModal(null, selectedRouteSlug, selectedDirection);
    });

    $(document).on('click', '[data-action="openSelectOriginPopup"]', function (event) {
        event.preventDefault();
        log('openSelectOriginPopup clicked');

        //Get state from URL
        const state = getStateFromUrl();
        if(!state.route) {
            log('showOriginSelectModal: Route required to get here');
            return false;
        }

        const route = getRouteBySlug(state.route);
        if(!route) {
            log('showOriginSelectModal: Route not found', state.route);
            return false;
        }
        
        const direction = state.direction || null;
        const selectedOriginSlug = state.origin || null;
        const selectedOriginId = selectedOriginSlug ? getPlaceIdBySlug(selectedOriginSlug) : null;
        
        // Show Origin Select Modal
        showOriginSelectModal(null, route.id, direction, selectedOriginId);
    });

    $(document).on('click', '[data-action="openSelectDestinationPopup"]', function (event) {
        event.preventDefault();
        log('openSelectDestinationPopup clicked');

        //Get state from URL
        const state = getStateFromUrl(); // Returns validatedPathIds but with returnSlugs=true
        if(!state.route || !state.origin) {
            log('showDestinationSelectModal: Route and origin required to get here');
            return false;
        }

        const route = getRouteBySlug(state.route);
        if(!route) {
            log('showDestinationSelectModal: Route not found', state.route);
            return false;
        }

        const origin = getPlaceBySlug(state.origin);
        if(!origin) {
            log('showDestinationSelectModal: Origin not found', state.origin);
            return false;
        }

        // Continue showing modal...
        
        const direction = state.direction || null;
        const selectedDestinationSlug = state.destination || null;
        const selectedDestinationId = selectedDestinationSlug ? getPlaceIdBySlug(selectedDestinationSlug) : null;

        const selectedViaIds = [];
        // viaArray is an array of route slugs
        if(state.viaArray && state.viaArray.length > 0) {
            state.viaArray.forEach(viaSlug => {
                const viaId = getRouteIdBySlug(viaSlug);
                if(viaId) {
                    selectedViaIds.push(viaId);
                }
            });
        }

        // Show Destination Select Modal
        showDestinationSelectModal(null, route.id, direction, origin.id, selectedDestinationId, selectedViaIds);
    });

    $(document).on('click', '[data-action="routeSelectModal_submit"]', function (event) {
        event.preventDefault();

        const selectedRouteId = $('#routeSelectModalContainer').find('input[name="routeSelect"]:checked').val();

        log('selectedRouteId', selectedRouteId);

        // Get route slug
        const route = getRouteById(selectedRouteId);
        log('route', route);

        if(route) {
            // Update the query param for the route.
            updateQueryParam('route', route.slug);

            // Handle direction.
            const selectedDirection = $('#routeSelectModalContainer').find('input[name="direction"]:checked').val();
            log('selectedDirection', selectedDirection);
            if(selectedDirection === 'reverse') {
                updateQueryParam('direction', 'reverse');
            } else {
                updateQueryParam('direction', null);
            }
        
            // Close the modal.
            closeFenixModal();

            return false;
        }
    });

    $(document).on('click', '[data-action="originSelectModal_submit"]', function (event) {
        event.preventDefault();

        const $button = $(this);
        let selectedOriginId = $button.attr('data-place-id') || null;
        if(!selectedOriginId) {
            selectedOriginId = $('#fenix').find('#placeSelectPlaceList').find('input[name="placeSelect"]:checked').val();
        }

        log('selectedOriginId', selectedOriginId);

        // Get the origin place by id.
        const originPlace = getPlaceById(selectedOriginId);
        log('originPlace', originPlace);

        // Update the query param for the origin.
        updateQueryParam('origin', originPlace.slug);
        
        // Close the modal.
        closeFenixModal();
        return false;
    });

    $(document).on('click', '[data-action="destinationSelectModal_submit"]', function (event) {
        event.preventDefault();
        const $button = $(this);
        let selectedDestinationId = $button.attr('data-place-id') || null; // For the direct select button in the read more text.
        if(!selectedDestinationId) {
            selectedDestinationId = $('#fenix').find('#placeSelectPlaceList').find('input[name="placeSelect"]:checked').val();
        }

        if(!selectedDestinationId) {
            log('destinationSelectModal_submit: No destination selected');
            return false;
        }

        let selectedViaIds = [];
        if($('#fenix').find('#placeSelectPlaceList').find('input[name="selectedChildRoutes"]:checked').length > 0) {
            $('#fenix').find('#placeSelectPlaceList').find('input[name="selectedChildRoutes"]:checked').each(function() {
                selectedViaIds.push(parseInt($(this).val()));
            });
        }
        // Convert selectedViaIds to an array of route slugs
        const selectedViaSlugs = selectedViaIds.map(viaId => getRouteById(viaId).slug);

        log('selectedDestinationId', selectedDestinationId);

        // Get the destination place by id.
        const destinationPlace = getPlaceById(selectedDestinationId);
        log('destinationPlace', destinationPlace);

        // Update the query param for the destination.
        updateQueryParam('destination', destinationPlace.slug, false);

        // Update the query param for the via slugs.
        if(selectedViaSlugs.length > 0) {
            updateQueryParam('via', selectedViaSlugs.join(','), false);
        } else {
            updateQueryParam('via', null, false);
        }

        // Update the map manually since we stopped it from happening inside updateQueryParam()
        updateMapFromUrl();
        
        // Close the modal.
        closeFenixModal();
        return false;
    });

    $(document).on('click', '[data-action="openChartPopup"]', function (event) {
        event.preventDefault();
        log('openChartPopup clicked');
        showElevationChart();
    });

    $(document).on('click', '[data-action="use-as-origin"]', function (event) {
        event.preventDefault();
        log('use-as-origin clicked');
        const $button = $(this);
        const postId = $button.data('use-as-origin-post-id') ?? null;
        
        // get place data
        const place = getPlaceById(postId);
        
        // Update the query param for the origin.
        if(place) {
            updateQueryParam('origin', place.slug);
        } else {
            log('place not found for id', postId);
        }
        
        // Close the modal.
        closeFenixModal();
        return false;
    });

    $(document).on('click', '[data-action="close-popup"]', function (event) {
        event.preventDefault();
        log('close-popup clicked');
        closeDesktopPopup();
        return false;
    });

    $(document).on('click', '[data-action="toggleMapStyleSelectModal"]', function (event) {
        event.preventDefault();
        log('toggleMapStyleSelectModal clicked');
        toggleMapStyleSelectModal();
        return false;
    });

    $(document).on('click', '[data-action="changeMapStyle"]', function (event) {
        event.preventDefault();
        log('changeMapStyle clicked');
        const selectedStyle = $(this).find('input').val();
        log('selectedStyle', selectedStyle);
        if(window.ironman.userData.permissions.allowedMapStyles.includes(selectedStyle)) {
            // Uncheck all other style input[type="radio"]
            $('#fenixBody').find('input[type="radio"][name="map-style"]').prop('checked', false);
            // Check the selected style
            $('#fenixBody').find('input[type="radio"][name="map-style"][value="' + selectedStyle + '"]').prop('checked', true);
            // Update the query param for the style.
            updateQueryParam('style', selectedStyle);
        } else {
            log('selectedStyle not allowed', selectedStyle);
        }
        
        return false;
    });

    $(document).on('change', 'input[name="marker-filters"]', function() {
        // Get the selected marker filter buttons.
        const selected = getSelectedMarkerFilterButtons();

        // Update the query param for the markers.
        updateQueryParam('markers', selected.join(','));

        // Show/hide the list view button.
        showHideListViewButton(selected);

        return false;
    });

    $(document).on('click', '[data-action="toggleRoutePreview"]', function (event) {
        event.preventDefault();
        log('toggleRoutePreview clicked');
        doMapboxFreeCameraAnimation();
        return false;
    });

    $(document).on('click', '[data-action="singlePlaceServiceIconClick"]', function (event) {
        event.preventDefault();
        log('singlePlaceServiceIconClick clicked');
        const $button = $(this);
        const serviceSlug = $button.data('service-slug');
        log('serviceSlug', serviceSlug);

        // Update the query param for the markers.
        // 1. Check if "markers:places" is in the query param. Keep it if so.
        const urlParams = new URLSearchParams(window.location.search);
        if(urlParams.get('markers')) {
            const markers = urlParams.get('markers').split(',');
            if(markers.includes('places')) {
                // Remove "places" from the query param.
                updateQueryParam('markers', 'places,' + serviceSlug);
                return false; // Finish here.
            }
        }
        updateQueryParam('markers', serviceSlug);
        return false;
    });

    $(document).on('click', '[data-action="openPaceSliderPopup"]', function (event) {
        event.preventDefault();
        log('openPaceSliderPopup clicked');
        openPaceSliderPopup();
        return false;
    });
    
    $(document).on('click', '[data-action="paceSliderModal_submit"]', function (event) {
        event.preventDefault();
        log('paceSliderModal_submit clicked');
        const $rangeSlider = $('#paceSliderModalContainer').find('[data-label="range-slider"]');
        const paceKmh = $rangeSlider.find('[data-label="range-slider-value"]').val() ?? window.ironman.paceKmh;
        log('paceKmh', paceKmh);

        // Update the global paceKmh value.
        window.ironman.paceKmh = Number(paceKmh);

        // Destroy the range slider.
        destroyRangeSliders($rangeSlider.parent().get(0));

        // Close the modal.
        closeFenixModal();

        // Update stats
        updateMapFromUrl(true);

        return false;
    });
}

/**
 * Gets state from URL and updates the map/page accordingly, downloading any necessary data.
 */
export function updateMapFromUrl(forceStatsUpdate = false, updateMapSidebar = true) {
    //Get state from URL
    const state = getStateFromUrl();
    log('Updating map from URL state:', state);

    // Compare with previous state to determine if an update is needed. Creates changedParams array.
    let changedParams = Object.keys(state).filter(key => {
        // Skip viaArray.
        if(key === 'viaArray') {
            return false;
        }
        // All state values are strings, so we can use strict equality.
        return previousState[key] !== state[key];
    });
    log('Changed params:', changedParams);

    // Update map style if it has changed - and then re-run this function.
    if(changedParams.includes('style')) {
        // Enable spinner. Disable in the style.load event.
        utils.updateLoadingSpinner('settingMapStyle', true, 'non-blocking', t('settingMapStyleSpinnerMessage'));

        updateMapStyle(state.style);
        // Calling updateMapStyle() will:
        // 1) remove the existing map lines sources and layers, and
        // 2) SHOULD trigger a style.load event, which SHOULD
        // 3) trigger createMapLinesSourcesAndLayers() which will
        // 4) create new map lines sources and layers, BUT
        // 5) just in case, we'll call it again here. It has a built in check to bail if it's already been done anyway.
        // Once this is all done, we need to call the updateMapFromUrl() function again to draw the lines.
        
        // Map lines sources and layers don't exist yet, so we need to wait for the style.load event to trigger createMapLinesSourcesAndLayers()
        mapInstance.once('style.load', function() {
            log('Style loaded, creating map lines sources and layers...');
            createMapLinesSourcesAndLayers();

            // But to avoid an infinite loop, we need to make sure it doesn't recognize a style change when it runs again.
            // So we are gonna set the previousState['style'] to the current state.style, and then updateMapFromUrl() will see that it's the same and bail out.
            // But we also need to clear all other params from previousState, to force a full re-draw but not change the style.
            previousState = {'style': state.style};
            updateMapFromUrl();
        });

        // Bail out of the function, it will start again once the style.load events triggers.
        return;
    }

    // Update UI components based on the state, e.g. route selection, direction, origin, destination, alternative routes, etc.
    updateMapPageUI(state);
    
    // If there are changes, update the map lines and stats
    // Style will never be seen as changed here, since it's handled above.
    if (
        changedParams.includes('route') || 
        changedParams.includes('direction') ||
        changedParams.includes('origin') ||
        changedParams.includes('destination') ||
        changedParams.includes('via') ||
        changedParams.includes('markers') ||
        (forceStatsUpdate === true)
    ) {

        // If markers have changed in the URL, update the buttons in the UI.
        // 1. Get the selected marker filters from the URL.
        const selectedMarkerFilterButtons = state.markers?.split(',') ?? [];
        // 2. Loop through the buttons and check/uncheck them based on the selected markers.
        updateMarkerFilterButtonsUI(selectedMarkerFilterButtons);
        // 3. Show/hide the list view button accordingly.
        showHideListViewButton(selectedMarkerFilterButtons);

        
        // Keep track of whether a full path is defined.
        let fullPathDefined = false;

        // Update path segments (if needed)
        if(state.route) {
            const route = getRouteBySlug(state.route);
            const routeId = route.id;

            // Update the route background map line if the route has changed.
            if(changedParams.includes('route')) {
                drawMapLines_RouteBackground(route.slug);
            }
            
            // Remove Santiago de Compostela marker if it exists
            if(santiagoMapMarker) {
                santiagoMapMarker.remove();
                santiagoMapMarker = null;
            }

            if(state.origin) {
                const origin = getPlaceBySlug(state.origin);
                const originId = origin.id;
                let originOnRoute = getPlaceAlongRoute(routeId, originId, true);
                
                if(state.destination) {
                    log('route, origin, and destination specified');
                    fullPathDefined = true;

                    // Route, Origin, and Destination are specified, calculate path segments
                    const destination = getPlaceBySlug(state.destination);
                    const destinationId = destination.id;
                    let destinationOnRoute = getPlaceAlongRoute(routeId, destinationId, true);

                    // Build pathfinder inputs
                    // let routeId = 50; // For testing, hardcoded route ID
                    // let originId = 1517; // For testing, hardcoded origin ID
                    // let destinationId = 1526; // For testing, hardcoded destination ID
                    let viaIds = [];
                    // Loop through string of via slugs and get the ids
                    if(state.via) {
                        state.via.split(',').forEach(slug => {
                            const via = getRouteIdBySlug(slug);
                            if(via) {
                                viaIds.push(via);
                            }
                        });
                    }

                    log(`Pathfinder inputs: Route: ${routeId}, Direction: ${state.direction}, Origin: ${originId}, Destination: ${destinationId}, Via: ${viaIds.join(',')}`);
                    // Run pathfinder.
                    // NOTE: pathfinder via expects [] of routeIds.
                    // NOTE: Run pathfinder BOTH if PATH or ONLY markers have changed. Easier than storing previous pathsegments. That's why this is here and not further down.

                    pathSegments = pathfinder(routeId, state.direction, originId, destinationId, viaIds);
                    log(`Pathfinder segments calculated:`, pathSegments);
                    
                    if(pathSegments === false) {
                        log('Pathfinder segments calculation failed');
                        return false;
                    }

                    // Calculate the path data from the pathfinder segments.
                    const pathData = calculatePathData(pathSegments);
                    log(`Path data calculated:`, pathData);

                    // Redraw map lines if the route has changed, but not if only the markers have changed.
                    if (
                        changedParams.includes('route') || 
                        changedParams.includes('direction') ||
                        changedParams.includes('origin') ||
                        changedParams.includes('destination') ||
                        changedParams.includes('via') 
                    ) {
                        // Draw map lines based on the path segments (ASYNC - needs to fetch mapserver data)
                        drawMapLines(route.slug, pathSegments, state.direction);

                        // Re-center map to fit bounds of origin and destination locations.
                        recenterMap(originOnRoute.lat, originOnRoute.lon, destinationOnRoute.lat, destinationOnRoute.lon);
                        log('Map re-centered to fit origin and destination locations.', originOnRoute, destinationOnRoute);

                        // Update map stats based on the state
                        updateMapStats(pathSegments, pathData);

                        // Update the map sidebar progress.
                        if(updateMapSidebar === true) {
                            updateMapSidebarProgress(routeId, state.direction, originId, destinationId, viaIds);
                        }

                        // Set 3D settings
                        setMap3dSettings(routeId, originId, null, state.direction);
                    } else {
                        // If forceStatsUpdate is true, update the map stats. Bypassing other updates.
                        if(forceStatsUpdate === true) {
                            updateMapStats(pathSegments, pathData);
                        }
                    }
                    
                    // Route changed: update markers to reflect new route.
                    log('Updating markers for state:', state, 'with path segments:', pathSegments);
                    updateMapMarkers(state, pathSegments);
                    
                    
                } else {
                    // Only origin specified
                    log('only origin specified');
                    // No destination. No path segments to calculate. Clear all lines.
                    clearMapLines();

                    recenterMap(originOnRoute.lat, originOnRoute.lon, null, null, 14);

                    // Route changed: update markers to reflect new route.
                    updateMapMarkers(state);

                    // Clear onPathLineString, no destination means no onPath.
                    onPathLineString = null;

                    log('should update elevation chart here. chartJsLoaded:', chartJsLoaded);
                    if(chartJsLoaded) {
                        //update the chart data if it exists.
                        updateElevationChartData();
                    }

                    // Disable elevation chart and route preview.
                    disableElevationChartAndRoutePreview();

                    // Set 3D settings
                    setMap3dSettings(routeId, originId, null, state.direction);

                }
            } else {
                // Only Route specified.
                log('only route specified');
                // No origin specified, so no path segments to calculate. 
                // Draw entire route line + child routes.

                drawMapLines(route.slug, null, state.direction); 

                // Re-center map to fit bounds of first and last places in route
                if(route.places && route.places.length > 0) {
                    const firstPlace = route.places[0];
                    const lastPlace = route.places[route.places.length - 1];
                    const firstPlaceOnRoute = getPlaceAlongRoute(routeId, firstPlace.id);
                    const lastPlaceOnRoute = getPlaceAlongRoute(routeId, lastPlace.id);
                    recenterMap(firstPlaceOnRoute.lat, firstPlaceOnRoute.lon, lastPlaceOnRoute.lat, lastPlaceOnRoute.lon);

                    // Set 3D settings
                    setMap3dSettings(routeId, firstPlace.id, null, state.direction);
                }

                // Route changed: update markers to reflect new route.
                updateMapMarkers(state);
            }
        } else {
            // Route not specified, draw all routes in low-res.
            log('route not specified');

            // Clear the route background map lines.
            clearMapLines_RouteBackground();

            // Draw all routes in low-res.
            drawMapLines('all', null, null);

            // Re-center map to starting position
            recenterMap(startingMapCenter[1], startingMapCenter[0], null, null, startingMapZoom);

            // Route cleared, update markers to reflect no route.
            updateMapMarkers(state);

            // Add a marker for Santiago de Compostela
            if(window.ironman?.appConfig?.santiago_map_marker) {
                santiagoMapMarker = drawSingleMapMarker(window.ironman.appConfig.santiago_map_marker.lat, window.ironman.appConfig.santiago_map_marker.long, window.ironman.appConfig.santiago_map_marker.name, 'santiago', window.ironman.appConfig.santiago_map_marker.post_id, null, 'santiago');
            }

            // Clear onPathLineString, no route means no onPath.
            onPathLineString = null;

            if (chartJsLoaded) {
                //update the chart data if it exists (should be empty)
                updateElevationChartData();
            }

            // Disable elevation chart and route preview.
            disableElevationChartAndRoutePreview();

            // Set 3D settings to default.
            setMap3dSettings();
        }

        if(fullPathDefined === false) {
            // Full path is not defined, clear the map route progress.
            mapRouteProgress.clearRouteLine();
        }
    } else {
        // No changes to route, direction, origin, destination, via, or markers.
    }
    
    // Save the current state as previous for next comparison
    previousState = state;
}

function recenterMap(lat1, lon1, lat2=null, lon2=null, zoom=startingMapZoom) {
    log('function:recenterMap', lat1, lon1, lat2, lon2, zoom);
    if (!lat2 || !lon2) {
        // If only one set of coordinates is provided, center the map on that point
        return mapInstance.flyTo({
            center: [lon1,lat1], 
            bearing: 0,
            pitch: 0,
            // linear: false,
            // speed: 1,
            zoom: zoom
        });
    } else {
        return mapInstance.fitBounds([[lon1,lat1],[lon2,lat2]], {
            padding: 100,
            bearing: 0,
            pitch: 0,
            // linear: false,
            // speed: .8
        });
    }
}

/**
 * Updates the Map Page UI based on the provided state.
 * @param {Object} state - The state object containing route, direction, origin, destination, etc.
 */
function updateMapPageUI(state) {
    log('updateMapPageUI', state);

    let routeSelected = false;
    let originSelected = false;
    let destinationSelected = false;

    // Update the select button text for the route, origin, and destination if they are set.
    if(state.route) {
        const route = getRouteBySlug(state.route);
        // Get Route Name from translated global array (fallback to DB object if not found)
        const routeName = window.ironman.appConfig.routeTaxonomy[state.route]?.name ?? route.text;
        // log('route', route);
        if(route) {
            $('button[data-label="select-route"]').text(routeName).removeClass('text-disabled').addClass('text-dark-emphasis');
            routeSelected = true;

            if(state.origin) {
                const origin = getPlaceBySlug(state.origin);
                // log('origin', origin);
                if(origin) {
                    $('button[data-label="select-origin"]').text(origin.text).removeClass('text-disabled').addClass('text-dark-emphasis');
                    originSelected = true;

                    // Show the filters toolbar
                    $('[data-label="map-toolbar-marker-filters"]').removeClass('d-none');

                    if(state.destination) {
                        const destination = getPlaceBySlug(state.destination);
                        // log('destination', destination);
                        if(destination) {
                            log('destination found, updating button text');
                            $('button[data-label="select-destination"]').text(destination.text).removeClass('text-disabled').addClass('text-dark-emphasis');
                            destinationSelected = true;

                            // Show the sidebar toolbar, stats, and stage stops.
                            $('[data-label="map-sidebar-toolbar"]').removeClass('d-none');
                            $('[data-label="map-sidebar-stats"]').removeClass('d-none');
                            $('[data-label="map-sidebar-stage-stops"]').removeClass('d-none');
                        }
                    }
                }
            }
        }
    }

    // Set default values for the select buttons if they are not selected.
    if(!routeSelected) {
        $('button[data-label="select-route"]').text($('button[data-label="select-route"]').data('default-text')).removeClass('text-dark-emphasis').addClass('text-disabled');
    }
    if(!originSelected) {
        $('button[data-label="select-origin"]').text($('button[data-label="select-origin"]').data('default-text')).removeClass('text-dark-emphasis').addClass('text-disabled');
        
        // Hide the filters toolbar
        $('[data-label="map-toolbar-marker-filters"]').addClass('d-none');
    }
    if(!destinationSelected) {
        $('button[data-label="select-destination"]').text($('button[data-label="select-destination"]').data('default-text')).removeClass('text-dark-emphasis').addClass('text-disabled');
        
        // Hide the sidebar toolbar, stats, and stage stops.
        $('[data-label="map-sidebar-toolbar"]').addClass('d-none');
        $('[data-label="map-sidebar-stats"]').addClass('d-none');
        $('[data-label="map-sidebar-stage-stops"]').addClass('d-none');
    }
}

/**
 * Updates the map style based on the provided style.
 * @param {string} style - The style to update the map to.
 */
function updateMapStyle(style) {
        mapInstance.setStyle(getMapStyle(style));
}

/**
 * Gets the map style url based on the provided style name.
 * @param {string} style - The style to get the map style for.
 * @param {boolean} validateOnly - If true, only validate the style and return true or false.
 * @returns {string} The map style url.
 * * If validateOnly is true, it will return true or false based on whether the style is valid.
 * * If validateOnly is false, it will return the map style url.
 * * If the style is invalid, it will return the default style url.
 */
function getMapStyle(style, validateOnly=false) {
    const allStyles = window.ironman.appConfig.mapStyles;
    if(validateOnly) {
        return allStyles[style] ? true : false;
    } else if(allStyles[style]) {
        return 'mapbox://styles/' + allStyles[style]['id'];
    } else {
        log(`Invalid style, using default: ${style}`);
        return allStyles[defaultMapStyle]['id'];
    }
}

/**
 * Draws the map lines based on the provided path segments.
 * @param {Array} pathSegments - An array of path segment objects, each containing coordinates and other properties.
 * * This function loops through the path segments and draws them on the map.
 * * It checks if each segment has coordinates and then creates a line feature for each segment.
 * * It works by creating a GeoJSON FeatureCollection from the path segments and setting it to the map source.
 * * The lines are styled based on their properties such as color and width.
 * */
function drawMapLines(routeSlug, pathSegments, direction='forward') {
    // Fetch route map data (either from cache or by downloading it)
    
    // note: fetchAPIData returns a Promise.
    fetchAPIData('mapserver', routeSlug, null, 'page-blocking', t('downloadingRouteMapDataSpinnerMessage')).then(function() {

        log('map data fetched successfully:', routeSlug);
        
        // Get the map data from the fetchedAPIData cache variable
        getFetchedAPIData('mapserver', routeSlug).then(function(mapData) {
            
            // DEBUG ONLY: Log the map data
            log('map data decrypted successfully:', mapData);

            // If routeSlug is 'all', draw all routes in low-res.
            if(routeSlug === 'all') {
                // Draw all routes in low-res.
                // mapData is already a single feature collection, ready to go.
                setMapSourceData(mapData);

                // Set cursor and click event
                mapInstance.on('mouseenter', 'segmentsLayer', changeMapMouseCursorPointer);
                mapInstance.on('mouseleave', 'segmentsLayer', changeMapMouseCursorNone);

                mapInstance.on('click', 'segmentsLayer', handleAllRoutesMapClick);
                
                log('map lines drawn for all routes in low-res.');
                return;
            } else {
                // Remove cursor and click event from all routes
                mapInstance.off('mouseenter', 'segmentsLayer', changeMapMouseCursorPointer);
                mapInstance.off('mouseleave', 'segmentsLayer', changeMapMouseCursorNone);
                mapInstance.off('click', 'segmentsLayer', handleAllRoutesMapClick);

                // Change cursor back to normal in case it was frozen on pointer bcz that's when the handlers disabled.
                changeMapMouseCursorNone();
            }

            const features = [];

            if(pathSegments === null) {
                // No path segments provided, draw the entire route line + child routes.

                //Extract the "feature" from each route in mapData
                Object.entries(mapData.routes).forEach(function([routeKey, route]) {

                    // let feature = route.geojson.features[0];
                    // Clone the feature to avoid modifying the original feature.
                    let feature = JSON.parse(JSON.stringify(route.geojson.features[0]));
                    
                    // Add properties to the feature
                    feature.properties = {
                        slug: route.slug,
                        state: 'route',
                        color: route.color || '#0000FF', // Default color if not specified
                        line_width: route.line_width || 3, // Default width if not specified
                        opacity: route.opacity || 1, // Default opacity if not specified
                        // dasharray: route.dasharray || [1,0], // Default dasharray if not specified
                        isDashed: false,
                        onPath: false // Default onPath value
                    };
                    // If this feature is the primary route, set onPath to true
                    if(routeKey === routeSlug) {
                        feature.properties.onPath = true;
                    } else {
                        // This is a child route
                        // feature.properties.color = '#577eff';
                        // feature.properties.opacity = 0.5;
                        // feature.properties.dasharray = [1,2];
                        feature.properties.isDashed = true;
                    }

                    if(feature) {
                        features.push(feature);
                    }
                });
            } else {
                log(`Drawing map lines for route: ${routeSlug} with ${pathSegments.length} segments.`, pathSegments);
                // Loop through path segments to create FeatureCollection for map source
                
                pathSegments.forEach(function(segment) {
                    let feature = getGeoJSONFeatureFromSegment(mapData, segment);
                    if(feature) {
                        features.push(feature);
                    }
                });
                log(`Created ${features.length} features from path segments.`, features);
            }

            // If there are valid features, create a FeatureCollection and set it to the map source
            if( features.length > 0) {
                // Create a FeatureCollection from the features array
                const featureCollection = makeFeatureCollection(features);

                // Set the data for the map source
                log('Setting map source data:', setMapSourceData(featureCollection), featureCollection);

                // Now that we have the map line data, we can also set the onPathLineString for elevation chart and route preview.
                onPathLineString = [];
                // Reverse the features array if the direction is reverse.
                if(direction === 'reverse') {
                    features.reverse();
                }
                features.forEach(function(feature) {
                    if(feature.properties.onPath) {
                        onPathLineString = onPathLineString.concat(feature.geometry.coordinates);
                    }
                });

                if(direction === 'reverse') {
                    onPathLineString.reverse();
                }
                // log('onPathLineString:', onPathLineString);

                // Enable elevation chart and route preview buttons.
                if(onPathLineString.length > 0) {
                    // log('onPathLineString is defined, enabling elevation chart and route preview.', onPathLineString);
                    enableElevationChartAndRoutePreview();
                    //Update Elevation Chart if it exists
                    if (chartJsLoaded) {
                        //update the chart data if it exists.
                        updateElevationChartData();
                    }
                    // Set the route line data for the map route progress.
                    // First, create a GeoJSON Feature from the onPathLineString.
                    const onPathLineStringFeature = {
                        type: 'Feature',
                        geometry: {
                            type: 'LineString',
                            coordinates: onPathLineString
                        }
                    };
                    log('Setting route line for map route progress:', mapRouteProgress.setRouteLine(onPathLineStringFeature));
                } else {
                    if (chartJsLoaded) {
                        //update the chart data if it exists (should be empty)
                        updateElevationChartData();
                    }
                    // Clear onPathLineString
                    onPathLineString = null;
                }
            } else {
                // No valid features created, clear the map lines
                clearMapLines();
            }
        });

    }, function(error) {
        log('Error fetching map data:', error);
        // Handle error, e.g. show an alert or fallback UI
    });
}

/**
 * Draws the route background map lines based on the provided route slug.
 * @param {string} routeSlug - The slug of the route to draw the background for.
 * * This function draws the route background map lines for the provided route slug.
 * * It works by fetching the map data for the route, extracting the route feature, and adding properties to it.
 * * It then creates a FeatureCollection from the features array and sets it to the map source.
 * Note: It's similar to drawMapLines, but it's for the route background, which is the whole route,so it doesn't handle path segments.
 */
function drawMapLines_RouteBackground(routeSlug) {
    // Fetch route map data (either from cache or by downloading it)
    
    // note: fetchAPIData returns a Promise.
    fetchAPIData('mapserver', routeSlug, null, 'page-blocking', t('downloadingRouteMapDataSpinnerMessage')).then(function() {

        log('map data fetched successfully:', routeSlug);
        
        // Get the map data from the fetchedAPIData cache variable
        getFetchedAPIData('mapserver', routeSlug).then(function(mapData) {
            
            // DEBUG ONLY: Log the map data
            // log('map data decrypted successfully:', mapData);

            // No path segments provided, draw the entire route line + child routes.
            const features = [];
            
            //Extract the "feature" from each route in mapData
            Object.entries(mapData.routes).forEach(function([routeKey, route]) {

                // let feature = route.geojson.features[0];
                // Clone the feature to avoid modifying the original feature.
                let feature = JSON.parse(JSON.stringify(route.geojson.features[0]));
                
                // Add properties to the feature
                feature.properties = {
                    slug: route.slug,
                    state: 'route',
                    color: route.color || '#0000FF', // Use same color as line, or default color if not specified
                    // line_width: 4,
                    // opacity: .1,
                };

                if(feature) {
                    features.push(feature);
                }
            });

            if( features.length > 0) {
                // Create a FeatureCollection from the features array
                const featureCollection = makeFeatureCollection(features);
                // Set the data for the map source
                log('Setting map source data:', setMapSourceData(featureCollection, 'routeBackgroundSource'), featureCollection);
            } else {
                // No valid features created, clear the map lines
                clearMapLines_RouteBackground();
            }
        });
    });
}

/**
 * Clears the map lines by setting an empty GeoJSON FeatureCollection.
 * */
function clearMapLines() {
    return setMapSourceData()
}

/**
 * Clears the route background map lines
 * */
function clearMapLines_RouteBackground() {
    return setMapSourceData(null, 'routeBackgroundSource');
}

/**
 * Sets the map source data for the segments.
 * @param {Object} data - The GeoJSON data to set as the source data.
 * * If no data is provided, it sets an empty FeatureCollection.
 */
function setMapSourceData(data=null, sourceId=null) {
    if(!data) {
        // If no data is provided, set an empty FeatureCollection
        data = { type: 'FeatureCollection', features: [] };
    }
    
    // Default to 'segmentsSource' if it's anything other than 'routeBackgroundSource'.
    if(sourceId !== 'routeBackgroundSource') {
        sourceId = 'segmentsSource';
    }

    return mapInstance.getSource(sourceId).setData(data);
}

function getGeoJSONFeatureFromSegment(mapData, segment) {
    // Check if segment is valid
    if (!segment || typeof segment !== 'object') {
        log('Invalid segment:', segment);
        return null;
    }
    // Get slugs from segment routeId and placeIds
    const route = getRouteById(segment.routeId);
    const firstPlaceId = segment.places[0].id; // First place in places array
    const lastPlaceId = segment.places[segment.places.length - 1].id; // Last place in places array
    const start = getPlaceById(firstPlaceId);
    const end = getPlaceById(lastPlaceId);

    let feature = createGeoJSONFeatureFromRouteSlice(mapData, route.slug, start.slug, end.slug);
    if (feature) {

        // Add properties to the feature
        feature.properties = {
            slug: route.slug,
            state: 'route-origin-destination',
            color: segment.color || '#0000FF', // Default color if not specified
            line_width: segment.line_width || 3, // Default width if not specified
            opacity: segment.opacity || 1, // Default opacity if not specified
            onPath: segment.onPath || false, // Default onPath value
            isDashed: false, // Default isDashed value
        };

        // Figure out if this segment is on the primary route or a child route
        if(utils.hasKey(route,'parent')) {
            // This is a child route
            // feature.properties.color = 'red';
            // feature.properties.dasharray = [1, 2];
            feature.properties.isDashed = true;
        }

        return feature;
    } else {
        log('Failed to create GeoJSON feature from segment:', segment);
        return null;
    }
}


/**
 * Extracts a LineString slice for one route between two place slugs.
 *
 * @param {Object} mapData
 * @param {string} routeSlug   – key of the route in mapData.routes
 * @param {string} startSlug   – key of the start place in route.places
 * @param {string} endSlug     – key of the end place in route.places
 * @returns {GeoJSON.Feature<LineString>}
 */
function createGeoJSONFeatureFromRouteSlice(mapData, routeSlug, startSlug, endSlug) {
    const route = mapData.routes[routeSlug];
    if (!route) {
        throw new Error(`Route "${routeSlug}" not found`);
    }

    const feature = route.geojson.features[0];
    const coords  = feature.geometry.coordinates;
    const places  = route.places;

    if (!(startSlug in places) || !(endSlug in places)) {
        throw new Error(`Place slug "${startSlug}" or "${endSlug}" not found`);
    }

    const startIdx = places[startSlug].index;
    const endIdx   = places[endSlug].index;

    // inclusive slice of coordinates
    const i0 = Math.min(startIdx, endIdx);
    const i1 = Math.max(startIdx, endIdx) + 1;
    const sliceCoords = coords.slice(i0, i1);

    return {
        type: 'Feature',
        geometry: {
            type: 'LineString',
            coordinates: sliceCoords
        },
        properties: {}
    };
}

/**
 * Wraps an array of GeoJSON Features in a FeatureCollection
 *
 * @param {Array<Object>} features  – list of GeoJSON Feature objects
 * @returns {GeoJSON.FeatureCollection}
 */
function makeFeatureCollection(features) {
    return {
        type: 'FeatureCollection',
        features: features
    };
}

/**
 * Updates the map markers based on the provided state.
 * @param {Object} state - The state object containing route, direction, origin, destination, etc.
 * @param {Array} pathSegments - The path segments to update the markers for.
 * * If no path segments are provided, the markers will be updated based on the state.
 * * PathSegments are only needed when there is a Destination specified.
 */
function updateMapMarkers(state, pathSegments=null) {
    log('function:updateMapMarkers', state, pathSegments);
    
    // Get markers from state.markers as an array of strings.
    let inputMarkers = [];
    if(state.markers && state.markers !== '') {
        inputMarkers = state.markers.split(',') || [];
    }

    // Initialize variables
    let routeId = null;
    let originId = null;
    let destinationId = null;
    let includedPlaceIds = [];

    if(state.route) {
        routeId = getRouteIdBySlug(state.route);
        if(state.origin) {
            originId = getPlaceIdBySlug(state.origin);
            if(state.destination) {
                // Origin and Destination specified.
                destinationId = getPlaceIdBySlug(state.destination);

                // Draw origin and destination markers.

                // But first, clear both so that they can be created without the possibility of conflict, e.g. if a place changes from destination to origin.
                updateOriginMarker(null);
                updateDestinationMarker(null);

                // Now, draw origin and destination markers.
                updateOriginMarker(routeId, originId);
                updateDestinationMarker(routeId, destinationId);
                // log('Origin and Destination markers updated.');

                // Draw all markers within pathSegments.
                if(pathSegments) {
                    // Add all places along pathSegments to includedPlaceIds.
                    // Including when onPath is false. Can always change later.
                    pathSegments.forEach(function(segment) {
                        segment.places.forEach(function(place) {
                            includedPlaceIds.push(place.id);
                        });
                    });
                } else {
                    log('Error: No path segments provided, cannot draw markers.');
                }
            } else {
                // Only Route and Origin specified.
                log('Only Route and Origin specified.', routeId, originId);
                
                // Delete destination marker first, in case it conflicts with creating the origin marker (if a place changes from destination to origin).
                updateDestinationMarker(null);

                // Draw origin marker.
                updateOriginMarker(routeId, originId);

                // Draw all markers in Origin place.
                includedPlaceIds.push(originId);
            }
        } else {
            // Only Route specified.
            // Draw all markers along route. No origin or destination markers.
            updateOriginMarker(null);
            updateDestinationMarker(null);

            // Draw all markers along route.

            // Add all places along route to includedPlaceIds.
            const route = getRouteById(routeId);
            Object.entries(route.places).forEach(function([placeKey, place]) {
                includedPlaceIds.push(place.id);
            });

            // Add all places along child routes to includedPlaceIds.
            const childRoutes = getChildRoutes(routeId);
            childRoutes.forEach(function(childRouteId) {
                const childRoute = getRouteById(childRouteId);
                Object.entries(childRoute.places).forEach(function([placeKey, place]) {
                    includedPlaceIds.push(place.id);
                });
            });
        }
    } else {
        // Route is not specified, clear all markers.
        updateOriginMarker(null);
        updateDestinationMarker(null);

        // Leave includedPlaceIds empty.
    }

    // Finished adding places to includedPlaceIds.
    log('includedPlaceIds:', includedPlaceIds);

    // Loop through inputMarkers and for each marker type, output a list of WP Post IDs that are getting markers.
    let newMarkers = {
        places: [],
        listings: []
    };

    // Handle Places
    // Doesn't require any new API data.
    const wantedPlaceTypes = getWantedMarkerTypesByCategory(inputMarkers, 'places');
    log('wantedPlaceTypes:', wantedPlaceTypes);
    if(wantedPlaceTypes.length > 0) {
        // Loop through includedPlaceIds and add the place to newMarkers.places if it's type is a wanted place type.
        includedPlaceIds.forEach(function(placeId) {
            const place = getPlaceById(placeId);
            if(wantedPlaceTypes.includes(place.type)) {
                newMarkers.places.push(placeId);
            }
        });
    }
    log('newMarkers.places:', newMarkers.places);

    // Handle Friends
    // Friends are all or nothing. No route or place filtering. No diffs. Which means it can be done async, unrelated to the other markers.
    if(inputMarkers.includes('friends')) {
        // Get friends API data
        fetchAPIData('friends', 'all', null, 'non-blocking', t('gettingFriendsDataSpinnerMessage')).then(function() {
            
            log('Friends API data fetched successfully:');
            
            // Get the friends data from the fetchedAPIData cache variable
            getFetchedAPIData('friends').then(function(friendsData) {
                
                log('friends data decrypted successfully:', friendsData);
                
                // Loop through friendsData.friends_by_place and draw friend markers for each placeId.
                Object.keys(friendsData.friends_by_place).forEach(function(placeId) {
                    let n=1;
                    let total=friendsData.friends_by_place[placeId].length;
                    friendsData.friends_by_place[placeId].forEach(function(friendId) {
                        // Draw friend marker for each friend
                        drawSingleFriendMarker(friendsData.friends[friendId], n, total);
                        n++;
                    });
                });

            }, function(error) {
                log('Error fetching listings data:', error);
                // Handle error, e.g. show an alert or fallback UI
            });
        });
    } else {
        // Clear all friends markers.
        Object.keys(friendMarkers).forEach(function(friendId) {
            friendMarkers[friendId].remove();
            delete friendMarkers[friendId];
        });
    }

    // Handle Listings
    // Get wanted place types from all Listing categories.
    // if there are wanted place types, we'll need to get the listings API data before we can get the Post IDs.

    const allowedMarkerTypes = getAllowedMarkers();
    // Loop through allowedMarkerTypes, run getWantedMarkerTypesByCategory for each category except 'places' and 'friends', and add the results to wantedMarkerTypes.
    let wantedListingTypes = [];
    Object.keys(allowedMarkerTypes).forEach(function(category) {
        if(category !== 'places' && category !== 'friends') {
            wantedListingTypes.push(...getWantedMarkerTypesByCategory(inputMarkers, category));
        }
    });
    log('wantedListingTypes:', wantedListingTypes);

    if((wantedListingTypes.length > 0) && (includedPlaceIds.length > 0)) {
        // Get listings API data
        fetchAPIData('listings', state.route, null, 'non-blocking', t('downloadingListingsDataSpinnerMessage')).then(function() {
            
            log('listings API data fetched successfully:', routeId);
            
            // Get the listings data from the fetchedAPIData cache variable
            getFetchedAPIData('listings', state.route).then(function(listingsData) {
                
                log('listings data decrypted successfully:', listingsData);
                
                // Loop through includedPlaceIds and check if listings_by_place has an entry for that placeId. If so, if any of the listing's services are in wantedMarkerTypes, add the listing id to newMarkers.listings.
                includedPlaceIds.forEach(function(placeId) {
                    if(listingsData.listings_by_place[placeId]) {
                        listingsData.listings_by_place[placeId].forEach(function(listingId) {
                            const listing = listingsData.listings[listingId];
                            if(listing.services.some(service => wantedListingTypes.includes(service))) {
                                //Confirm the listing HAS GPS coordinates.
                                if(utils.hasKey(listing, 'map_marker_gps_lat') && utils.hasKey(listing, 'map_marker_gps_long') && listing.map_marker_gps_lat !== null && listing.map_marker_gps_long !== null) {
                                    newMarkers.listings.push(parseInt(listingId));
                                }
                            }
                        });
                    }
                });

                // Update the map markers.
                doUpdateMapMarkers(newMarkers, routeId, listingsData);

            }, function(error) {
                log('Error fetching listings data:', error);
                // Handle error, e.g. show an alert or fallback UI
            });
        });

    } else {
        // No wanted place types, so we can skip the listings API data.
        doUpdateMapMarkers(newMarkers, routeId, null);
    }

    // Then we draw the difference.
}

function doUpdateMapMarkers(newMarkers, routeId, listingsData=null) {
    // Combine all the newMarkers which are WP Posts into a single array.
    const allNewMarkers = [...newMarkers.places, ...newMarkers.listings];
    log('allNewMarkers:', allNewMarkers);

    //WARNING: Ids in mapMarkers were converted to strings.

    // Compare allNewMarkers to mapMarkers and output 3 lists: 
    // 1) markersToAdd: WP Post IDs that are in allNewMarkers but not in mapMarkers.
    // 2) markersToRemove: WP Post IDs that are in mapMarkers but not in allNewMarkers.
    // 3) markersToKeep: WP Post IDs that are in both allNewMarkers and mapMarkers.
    const markersToAdd = allNewMarkers.filter(markerId => !mapMarkers[markerId]);
    let markersToRemove = Object.keys(mapMarkers).filter(markerId => !allNewMarkers.includes(parseInt(markerId)));
    const markersToKeep = Object.keys(mapMarkers).filter(markerId => allNewMarkers.includes(parseInt(markerId)));

    // Remove origin and destination ids from markersToRemove.
    if(originMapMarkerPlaceId && markersToRemove.includes(originMapMarkerPlaceId.toString())) {
        markersToRemove = markersToRemove.filter(markerId => markerId !== originMapMarkerPlaceId.toString());
    }
    if(destinationMapMarkerPlaceId && markersToRemove.includes(destinationMapMarkerPlaceId.toString())) {
        markersToRemove = markersToRemove.filter(markerId => markerId !== destinationMapMarkerPlaceId.toString());
    }

    log('markersToAdd:', markersToAdd);
    log('markersToRemove:', markersToRemove);
    log('markersToKeep:', markersToKeep);
    log('newMarkers:', newMarkers);

    // From each newMarkers type, remove the markers that are in markersToKeep. So we don't try to add them when they're already there.
    markersToKeep.forEach(function(markerId) {
        for(const type in newMarkers) {
            // log('attempting to remove markerId:', markerId, 'from type:', type, 'from newMarkers array.');
            if(newMarkers[type].includes(parseInt(markerId))) {
                log('removing markerId:', markerId, 'from type:', type, 'from newMarkers array.');
                utils.removeItemFromArray(newMarkers[type], parseInt(markerId));
            }
        }
    });

    // Remove the markersToRemove.
    markersToRemove.forEach(function(markerId) {
        const marker = mapMarkers[markerId.toString()];
        if(marker) {
            marker.remove();
            delete mapMarkers[markerId.toString()];
        }
    });
    
    // Draw markers from each newMarkers type.
    log('drawing markers from newMarkers array.', newMarkers);
    for(const type in newMarkers) {
        newMarkers[type].forEach(function(markerId) {
            if(type === 'places') {
                drawSinglePlaceMarker(routeId, markerId);
            } else if(type === 'listings') {
                drawSingleListingMarker(listingsData.listings[markerId]);
            }
        });
    }

    // Open marker popup with id in ?show_marker_id=
    log('finished drawing markers?');
    // Check if onPageLoadOpenMarkerId is set.
    if(onPageLoadOpenMarkerId) {
        const markerId = onPageLoadOpenMarkerId;
        // Clear the global var so this doesn't run again.
        onPageLoadOpenMarkerId = null;
        
        // Find the marker element by id.
        const $marker = $(document).find('.ironman-map-marker[data-marker-mixed-id="'+markerId+'"]');
        if($marker.length > 0) {
            const markerEl = $marker.get(0);
            handleMapMarkerClick(markerEl);
            // Zoom to the marker.
            recenterMap(markerEl.getAttribute('data-marker-lat'), markerEl.getAttribute('data-marker-long'), null, null, 15);
        }
    }
}

/**
 * Gets the wanted marker types by category.
 * @param {Array} markers - The input markers provided by the user.
 * @param {string} category - The category to search.
 * @returns {Array} The wanted marker types.
 */
function getWantedMarkerTypesByCategory(markers, category) {
    const markerCategories = getAllowedMarkers();
    
    // if markers includes a category, return all the sub‐items in that category.
    if(markers.includes(category)) {
        return markerCategories[category];
    }
    // otherwise, return an array of sub‐items that are wanted.
    return markers.filter(marker => markerCategories[category].includes(marker));
}

/**
 * Updates the origin marker on the map.
 * @param {number} routeId - The ID of the route the origin is on.
 * @param {number} originId - The ID of the origin place.
 */
function updateOriginMarker(routeId = null, originId = null) {
    if(originId === originMapMarkerPlaceId) {
        // No change, do nothing.
        return;
    }

    // Clear existing origin marker (if there is one)
    if(mapMarkers[originMapMarkerPlaceId]) {
        mapMarkers[originMapMarkerPlaceId].remove();
        delete mapMarkers[originMapMarkerPlaceId];
    }
    if(originMapMarkerPlaceId) {
        originMapMarkerPlaceId = null;
    }

    // If route and origin are provided, create a new origin marker
    if (routeId && originId) {
        log('updateOriginMarker: Creating new origin marker', routeId, originId);
        const newMarker = drawSinglePlaceMarker(routeId, originId, 'origin');
        if(newMarker) {
            originMapMarkerPlaceId = originId;
            // log('Origin marker updated.');
        } else {
            // Failed or no change.
            // log('Failed to update origin marker.');
        }
    }
}

/**
 * Updates the destination marker on the map.
 * @param {number} routeId - The ID of the route the destination is on.
 * @param {number} destinationId - The ID of the destination place.
 */
function updateDestinationMarker(routeId = null, destinationId = null) {
    if(destinationId === destinationMapMarkerPlaceId) {
        // No change, do nothing.
        return;
    }

    // Clear existing destination marker (if there is one)
    if(mapMarkers[destinationMapMarkerPlaceId]) {
        mapMarkers[destinationMapMarkerPlaceId].remove();
        delete mapMarkers[destinationMapMarkerPlaceId];
    }
    if(destinationMapMarkerPlaceId) {
        destinationMapMarkerPlaceId = null;
    }

    // If route and destination are provided, create a new destination marker
    if (routeId && destinationId) {
        const newMarker = drawSinglePlaceMarker(routeId, destinationId, 'destination');
        if(newMarker) {
            destinationMapMarkerPlaceId = destinationId;
        } else {
            // Failed or no change.
        }
    }
}

/**
 * Draws a single place marker on the map.
 * @param {number} routeId - The ID of the route the place is on.
 * @param {number} placeId - The ID of the place.
 * @param {string} markerType - The type of marker to draw.
 */
function drawSinglePlaceMarker(routeId, placeId, markerType = 'place') {
    const place = getPlaceById(placeId);
    if(place.type === 'intersection') {
        // Intersections don't show markers.
        return null;
    }
    let placeAlongRoute = getPlaceAlongRoute(routeId, placeId, true);

    if (!place || !placeAlongRoute) {
        log(`Place not found for slug: ${placeId}`);
        return false;
    }

    // Create a new marker for the place if it doesn't already exist.
    if (!utils.hasKey(mapMarkers, placeId)) {
        log('drawSinglePlaceMarker: Creating new place marker', placeAlongRoute.lat, placeAlongRoute.lon, place.text, markerType, placeId, routeId);
        const marker = drawSingleMapMarker(placeAlongRoute.lat, placeAlongRoute.lon, place.text, markerType, placeId, routeId, (place.type ?? 'place'));
        // WARNING: Object keys are always converted to strings.
        mapMarkers[placeId] = marker;
        // log('Place marker added.', placeId, place.id, marker);
        return marker;
    } else {
        log('drawSinglePlaceMarker: Place marker already exists', placeId, mapMarkers[placeId]);
        log(originMapMarkerPlaceId, destinationMapMarkerPlaceId);
    }
    return null;
}

function drawSingleListingMarker(listing) {
    log('Drawing listing marker:', listing);
    // Create a new marker for the listing if it doesn't already exist.
    if (listing && !utils.hasKey(mapMarkers, listing.id)) {

        // Get most relevant marker icon for the listing.
        let markerIcon = 'default';
        if(typeof listing.service_types === 'object' && listing.service_types.length > 0) {
            // Get the most relevant marker icon for the listing.
            // Loop through marker types in order of priority and use the first one that matches.
            for(const markerType of window.ironman.appConfig.mapMarkerPriority) {
                if(listing.service_types.includes(markerType)) {
                    markerIcon = markerType;
                    break;
                }
            }
        }

        // Get the GPS coordinates from the listing.location_gps field, e.g. "location_gps": "43.1086215,-1.2826902"
        const marker = drawSingleMapMarker(listing.map_marker_gps_lat, listing.map_marker_gps_long, listing.title, 'listing', listing.id, null, markerIcon);
        // WARNING: Object keys are always converted to strings.
        mapMarkers[listing.id] = marker;
        // log('Place marker added.', placeId, place.id, marker);
        return marker;
    }
    return null;
}

/**
 * Draws a single friend marker on the map.
 * @param {Object} friend - The friend object.
 * @param {number} position - The position of the friend in the list.
 * @param {number} total - The total number of friends at the checkin place.
 * @returns {Object} The marker object.
 * Position and total are used to offset the markers around the place GPS coordinates.
 */
function drawSingleFriendMarker(friend, position=1, total=1) {
    log('Drawing friend marker:', friend, position, total);
    // Create a new marker for the friend if it doesn't already exist.
    if (friend && !utils.hasKey(friendMarkers, friend.id)) {
        // Get the GPS coordinates from the friend.checkinPlaceId
        const place = getPlaceById(friend.checkinPlaceId);
        log('Checkin Place:', place);
        if(place && place.map_marker_gps_lat && place.map_marker_gps_long) {

            // Calculate the offset for the marker to stagger the markers around the place GPS coordinates.
            const baseLngLat = [place.map_marker_gps_long, place.map_marker_gps_lat];
            // Initial offset calculation
            const offsetLngLat = (total > 1)
                ? staggerMarkerLngLat(baseLngLat, position, total)
                : baseLngLat;

            // Draw the marker.
            log('Drawing friend marker:', friend.name, offsetLngLat);
            const marker = drawSingleMapMarker(offsetLngLat[1], offsetLngLat[0], friend.name, 'friend', friend.id, null, 'friend');
            // WARNING: Object keys are always converted to strings.
            friendMarkers[friend.id] = marker;

            // Add a face to the marker.
            const svg = marker.getElement().getElementsByTagName('svg')[0];
            svg.setAttribute('width', '100');
            svg.setAttribute('height', '100');
            const thisMarker = marker.getElement();
            thisMarker.innerHTML = thisMarker.innerHTML + '<img src="'+friend.profilePictureURL+'" class="ironman-friendMarker-thumbnail">';

            return marker;
        } else {
            log('Checkin Place not found for friend:', friend);
            return null;
        }
    }
    return null;
}

/**
 * Stagger a marker around a base GPS coordinates.
 * @param {Array} baseLngLat - The base longitude and latitude of the place.
 * @param {number} position - The position of the marker in the list.
 * @param {number} total - The total number of markers at the place.
 * @returns {Array} The offset longitude and latitude.
 */
function staggerMarkerLngLat(baseLngLat, position, total) {
    // ---------- TUNING ----------
    const BASE_RADIUS_METERS   = 900;   // spacing for small groups
    const RADIUS_GROWTH_PER_PIN= 60;   // extra meters per additional pin
    const MAX_RADIUS_METERS    = 2000;  // cap so pins don't drift away
    const START_ANGLE_RADIANS  = -Math.PI / 2; // first pin at top
    // ----------------------------
  
    // Coerce inputs to numbers (DBs often give strings)
    const baseLng = Number(baseLngLat?.[0]);
    const baseLat = Number(baseLngLat?.[1]);
    const pos     = Number(position);
    const cnt     = Number(total);
  
    // Bail safely if anything is unusable
    if (!isFinite(baseLng) || !isFinite(baseLat) || !isFinite(pos) || !isFinite(cnt) || cnt <= 1) {
      return [baseLng, baseLat];
    }
  
    // Radius based on group size
    const radiusMeters = Math.min(
        MAX_RADIUS_METERS,
        BASE_RADIUS_METERS + Math.max(0, cnt - 2) * RADIUS_GROWTH_PER_PIN
    );
  
    // Evenly spaced angle for this pin (normalize position to 0..cnt-1)
    const index = ((Math.floor(pos) - 1) % cnt + cnt) % cnt;
    const angle = (2 * Math.PI * index) / cnt + START_ANGLE_RADIANS;
  
    // Convert meters to degrees at this latitude
    const METERS_PER_DEG_LAT = 111320; // approx
    const cosLat = Math.cos((baseLat * Math.PI) / 180);
    const metersPerDegLng = Math.max(Math.abs(METERS_PER_DEG_LAT * cosLat), 1e-9); // avoid div/0 near poles
  
    const metersEast  = radiusMeters * Math.cos(angle);
    const metersNorth = radiusMeters * Math.sin(angle);
  
    const dLat = metersNorth / METERS_PER_DEG_LAT;
    const dLng = metersEast  / metersPerDegLng;
  
    return [baseLng + dLng, baseLat + dLat];
}  
  

/**
 * Draws a single map marker on the map.
 * @param {number} lat - The latitude of the marker.
 * @param {number} long - The longitude of the marker.
 * @param {string} name - The name of the marker.
 * @param {string} markerType - The type of marker to draw.
 * @param {number} mixedId - The mixed ID of the source the marker is for. Could be a post id (e.g. place id, listing id) or a user id (e.g. friend id) or null.
 * @param {number} routeId - The ID of the route the marker is on. Only used for places along a route.
 */
function drawSingleMapMarker(lat, long, name = 'unknown', markerType = 'default', mixedId = null, routeId = null, markerIcon = 'default') {

    // Get the marker background image URL from the app config.
    const markerStyle = window.ironman.appConfig.mapMarkers[markerIcon] || window.ironman.appConfig.mapMarkers['default'];
    log('drawSingleMapMarker: markerStyle', markerStyle);

    // Custom Marker HTML
    // Create a DOM element for each marker.
    const el = document.createElement('div');
    const width = markerStyle.width || 40;
    const height = markerStyle.height || 40;
    const anchor = markerStyle.anchor || 'center';
    el.className = 'ironman-map-marker ironman-map-marker-' + markerType;
    el.setAttribute('data-marker-type', markerType);
    el.setAttribute('data-marker-mixed-id', mixedId);
    el.setAttribute('data-marker-lat', lat);
    el.setAttribute('data-marker-long', long);
    el.tabIndex = 0;
    if(routeId) {
        // Only for places along a route.
        el.setAttribute('data-marker-route-id', routeId);
    }

    if(markerStyle.type === 'image') {
        el.style.backgroundImage = `url(`+markerStyle.url+`)`;
        el.style.backgroundSize = '100%';
    } else if(markerStyle.type === 'icon') {
        // Alternate to using a background image.
        el.style.backgroundColor = markerStyle.bgColor || 'transparent';
        el.style.backgroundImage = markerStyle.bgImage || 'none';
        el.style.boxShadow = markerStyle.boxShadow || '#ababab 0px 1px 4px 2px';
        el.style.borderRadius = '50%';
        el.style.border = markerStyle.border || '3px solid #fff';
        el.style.fontSize = '16px';
        el.style.textAlign = 'center';
        el.style.paddingTop = '2px';
        el.style.color = markerStyle.color || '#fff';
        el.innerHTML = `<i class="`+markerStyle.icon+`"></i>`;
    }

    // Set common styles
    el.style.width = `${width}px`;
    el.style.height = `${height}px`;

    return new mapboxgl.Marker({
        element: el,
        anchor: anchor
    })
        .setLngLat([long, lat])
        .addTo(mapInstance);
    
}

/**
 * Updates the map stats based on the provided state.
 * @param {Object} state - The state object containing route, direction, origin, destination, etc.
 */
function updateMapStats(pathSegments, pathData) {
    // Update the total distance.
    $('[data-label="map-sidebar-stats"]').find('[data-label="total-distance"]').text(displayLongDistance(pathData.distance, 'metric'));
    // Update the ascent.
    $('[data-label="map-sidebar-stats"]').find('[data-label="ascent"]').text(displayShortDistance(pathData.ascent, 'metric'));
    // Update the descent.
    $('[data-label="map-sidebar-stats"]').find('[data-label="descent"]').text(displayShortDistance(pathData.descent, 'metric'));

    // Update the est. travel time from distance in meters.
    let pace = window.ironman.paceKmh; // km/h

    const paceMs = pace * 1000 / 3600; // convert to m/s
    let estTravelTime = pathData.distance / paceMs; // in seconds
    let estTravelTimeHours = Math.floor(estTravelTime / 3600);
    let estTravelTimeMinutes = Math.floor((estTravelTime % 3600) / 60);
    // let estTravelTimeSeconds = Math.floor(estTravelTime % 60);
    let estTravelTimeString = `${estTravelTimeHours}h ${estTravelTimeMinutes}m`;
    $('[data-label="map-sidebar-stats"]').find('[data-label="est-travel-time"]').text(estTravelTimeString);

}

function updateMapSidebarProgress(routeId, direction, originId, destinationId, viaIds=[]) {
    log('updateMapSidebarProgress', routeId, direction, originId, destinationId, viaIds);

    // Start customizing

    // Get the place list container.
    const $placeList = $('[data-label="map-sidebar-stage-stop-list"]').find('ul.stage-stops-container');

    // Build the route places list every time the modal is opened.
    const allRoutePlaces = getRoutePlaces(routeId, direction, originId);
    log('allRoutePlaces', allRoutePlaces);

    let listType = 'stoplist';
    // $placeList.attr('data-list-type', listType);

    // Add child route places to the route places.
    let filteredRoutePlaces = addChildRoutePlaces(allRoutePlaces, direction);

    // Slice the route places from the origin.
    if(originId) {
        filteredRoutePlaces = sliceRoutePlacesFromOrigin(filteredRoutePlaces, originId);

        if(destinationId) {
            filteredRoutePlaces = sliceRoutePlacesToDestination(filteredRoutePlaces, destinationId);
        }

        // Add baseline calculated values from the origin (Distance, Ascending, Descending)
        filteredRoutePlaces = addCalculatedValuesFromOrigin(filteredRoutePlaces, routeId, originId);
    }

    // Save to global var (for future reference, e.g. search filtering, recalculating distances on toggle change..)
    setGlobalPathStops('mapSidebarStoplist', filteredRoutePlaces);

    log('filteredRoutePlaces', filteredRoutePlaces, listType);
    const listFragment = renderFilteredPlaceList(filteredRoutePlaces, listType, null, originId, destinationId, viaIds, routeId, false, false, true, true, false);
    
    // Write list to the ul container.
    $placeList.empty().append(listFragment);

}

/**
 * Converts current URL into a structured state object and validates the state.
 * @returns {Object} - The state object.
 */
function getStateFromUrl() {
    const p = new URLSearchParams(window.location.search);
    log('getStateFromUrl: ', p);
    // log('direction: ', p.get('direction'));
    // log('origin: ', p.get('origin'));
    // log('destination: ', p.get('destination'));
    // log('via: ', p.get('via'));
    // log('markers: ', p.get('markers'));
    // log('style: ', p.get('style'));

    // Validate user Path input and return params object.
    let params = validatePathSlugs(p, true);
    log('Validated Path Params:', params);
    
    // Handle Markers

    //validate markers against allowed markers
    // 1) Define your categories and their allowed sub‐items:
    const allowedMarkers = getAllowedMarkers();

    // 2) Parse & normalize the incoming param:
    let markersInput = (p.get('markers') || '');
    let markers = markersInput
        .split(',')
        .map(m => m.trim().toLowerCase())
        .filter(Boolean);

    // 3) Keep only entries that are either a category *or* one of its sub‐items:
    markers = markers.filter(m =>
        m in allowedMarkers || Object.values(allowedMarkers).some(subs => subs.includes(m))
    );

    // 4) Dedupe and enforce “category wins over its items”:
    const markerSet = new Set(markers);
    for (const [category, items] of Object.entries(allowedMarkers)) {
        if (markerSet.has(category)) {
            // remove any individual items in that category
            items.forEach(item => markerSet.delete(item));
        }
    }

    // 5) Final validated array
    // params.markers = Array.from(markerSet);
    //  Return a string of comma separated values.
    params.markers = null;
    if(markerSet.size > 0) {
        params.markers = Array.from(markerSet).join(',');
    }

    // Validate Show options
    // const allowedShowOptions = ['list', 'preview', '3d', 'elevation'];
    // params.show = (p.get('show') || '').split(',').filter(Boolean);
    // //validate show options against allowed options
    // params.show = params.show.filter(option => allowedShowOptions.includes(option));

    //Map Style
    params.style = defaultMapStyle;
    const inputStyle = p.get('style');
    if(getMapStyle(inputStyle, true) && window.ironman.userData.permissions.allowedMapStyles.includes(inputStyle)) {
        params.style = inputStyle;
    }

    // Normalize query params and update url with normalized params, without adding a new history entry.
    let validatedURL = getPathQueryString(params);
    
    if(params.route) {
        // Markers and Show requires at least a route.
        if(params.markers) {
            if(validatedURL.includes('?')) {
                validatedURL += '&';
            } else {
                validatedURL += '?';
            }
            validatedURL += 'markers=' + params.markers;
        }
        // if(params.show.length > 0) {
        //     validatedURL += '&show=' + params.show.join(',');
        // }
    }

    // Allow style to be set with or without a route.
    // Params.style is already validated when set and confirmed user has permission to change style.
    if((params.style !== defaultMapStyle) && window.ironman.userData.permissions.allowedMapStyles.includes(params.style)) {
        if(validatedURL.includes('?')) {
            validatedURL += '&';
        } else {
            validatedURL += '?';
        }
        validatedURL += 'style=' + params.style;
    }
    
    
    if(validatedURL !== window.location.search) {
        history.replaceState(null, '', `${window.location.pathname}${validatedURL}`);
    }
    
    return params;
}

/**
 * Updates a single query param and triggers Map update
 * @param {string} key - The key of the query param to update.
 * @param {string} value - The value of the query param to update.
 * @param {boolean} updateMap - Whether to update the map from the new URL state. NOTE: Optional so that we can call this function multiple times in a row, and just update the map once when we're finished, vs. updating each time.
 */
export function updateQueryParam(key, value, updateMap=true) {
    const allowedKeys = ['route', 'direction', 'origin', 'destination', 'via', 'markers', 'style'];
    if(!allowedKeys.includes(key)) {
        log('Invalid query param key:', key);
        return;
    }
    const p = new URLSearchParams(window.location.search);
    if (!value || (Array.isArray(value) && value.length === 0)) {
        p.delete(key);
    } else {
        p.set(key, Array.isArray(value) ? value.join(',') : value);
    }

    // Clear dependent path variables that may be affected by the change.
    if(key === 'route') {
        p.delete('direction');
        p.delete('origin');
        p.delete('destination');
        p.delete('via');
        p.delete('markers');
    } else if(key === 'direction') {
        p.delete('origin');
        p.delete('destination');
        p.delete('via');
    } else if(key === 'origin') {
        p.delete('destination');
        p.delete('via');
    } else if(key === 'destination') {
        p.delete('via');
    }
  
    history.pushState({}, '', `${window.location.pathname}?${p.toString()}`);
    
    // Update the map from the new URL state
    // note: This IS needed because the popstate event listener is not triggered by this function, even though it changes the URL.
    if(updateMap) {
        updateMapFromUrl();
    }
}
//Make this function available globally for debugging
// window.updateQueryParam = updateQueryParam;

/**
 * Changes the map mouse cursor to pointer.
 */
function changeMapMouseCursorPointer() {
    mapInstance.getCanvas().style.cursor = 'pointer';
}

/**
 * Changes the map mouse cursor to none.
 */
function changeMapMouseCursorNone() {
    mapInstance.getCanvas().style.cursor = '';
}

/**
 * Handles the click event for the all routes layer.
 * @param {Object} e - The click event object.
 */
function handleAllRoutesMapClick(e) {
    log('Map clicked at:', e.lngLat);
    // Handle map click events, e.g. show popup or marker
    // e.features is an array; we’ll grab the first hit
    const feature = e.features[0];
    const props   = feature.properties;
    if(props.state === 'all-routes') {
        log('Routes clicked, updating query param', props);
        if(props.route_type === 'child') {
            // Get slug of parent route.
            const parentRoute = getRouteById(props.parent_route);
            updateQueryParam('route', parentRoute.slug);
        } else {
            updateQueryParam('route', props.slug);
        }
    }
}

async function toggleElevationChart() {
    log('toggleElevationChart');

    // Initialize the elevation chart offcanvas if it doesn't exist.
    if(!elevationChartOffcanvas) {
        const BS = getBootstrap();
        if (!BS || !BS.Offcanvas) {
            // Bootstrap not loaded yet — just bail out safely
            log('toggleElevationChart: Bootstrap not loaded yet');
            return false;
        }

        // Make sure the elevation chart offcanvas element exists.
        const elevationChartOffcanvasElement = $('#ironmanElevationChart')[0];
        if (!elevationChartOffcanvasElement) {
            log('toggleElevationChart: Elevation chart offcanvas element not found');
            return false;
        }

        // Initialize the elevation chart offcanvas.
        elevationChartOffcanvas = new BS.Offcanvas(elevationChartOffcanvasElement, {
            backdrop: false
        });
    }

    // Toggle offcanvas
    elevationChartOffcanvas.toggle();

    // Load Chart.js and init chart only the first time
    if (!chartJsLoaded) {
        try {
            // Dynamic import of Chart.js
            const { Chart } = await import('chart.js/auto');
            console.log("ChartJS:", Chart);
            chartJsLoaded = true;
            console.log("New chartJsLoaded state:", chartJsLoaded);
            initElevationChart(Chart);
        } catch (error) {
            console.error('Failed to load Chart.js:', error);
        }
        // ChartJS Loaded. Update the chart data.
        updateElevationChartData();
    } else {
        //update the chart data
        updateElevationChartData();
    }

    // Remove the elevation marker. (In case this toggle is closing it and the marker is still on the map.)
    removeElevationMarker();
}

// For use with console, because toggleElevationChart is async it needs a callback.
function showElevationChart() {
    toggleElevationChart().then(() => console.log('Chart loaded successfully')).catch(error => console.error('Error:', error));
}
// window.showElevationChart = showElevationChart;

function initElevationChart(Chart) {
    const data = [
        // { x: 0.0, y: 200 }, { x: 0.1, y: 205 }, { x: 0.2, y: 210 },
        // { x: 0.3, y: 215 }, { x: 0.4, y: 220 }, { x: 0.5, y: 225 },
        // { x: 0.6, y: 230 }, { x: 0.7, y: 235 }, { x: 0.8, y: 240 },
        // { x: 0.9, y: 245 }, { x: 1.0, y: 250 }, { x: 1.1, y: 260 },
        // { x: 1.2, y: 270 }, { x: 1.3, y: 280 }, { x: 1.4, y: 290 },
        // { x: 1.5, y: 295 }
    ];
          
    const verticalLinePlugin = {
        id: 'hoverLine',
        afterDraw(chart) {
            const hoverX = elevationChartHoverX;
            if (hoverX !== null) {
                const ctx = chart.ctx;
                const xScale = chart.scales.x;
                const topY = chart.scales.y.top;
                const bottomY = chart.scales.y.bottom;
    
                ctx.save();
                ctx.beginPath();
                ctx.moveTo(hoverX, topY);
                ctx.lineTo(hoverX, bottomY);
                ctx.lineWidth = 1;
                ctx.strokeStyle = 'rgba(0,0,0,0.3)';
                ctx.stroke();
                ctx.restore();
            }
        }
    };
  
    const ctx = document.getElementById('elevationChart').getContext('2d');
  
    elevationChartInstance = new Chart(ctx, {
        type: 'line',
        data: {
            datasets: [{
                label: 'Elevation (m)',
                data: data,
                fill: true,
                backgroundColor: 'rgba(0,123,255,0.2)',
                borderColor: 'rgba(0, 123, 255, 1)',
                tension: 0.4,
                pointRadius: 0,
                pointHoverRadius: 6,
                pointBackgroundColor: 'transparent',
                pointBorderColor: 'transparent',
                pointHoverBackgroundColor: 'white',
                pointHoverBorderColor: 'red',
                pointHoverBorderWidth: 2
            }]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
                mode: 'nearest',
                intersect: false,
                axis: 'x'
            },
            onHover: (event, elements) => {
                if (elements.length > 0) {
                    const index = elements[0].index;
                    const point = elevationChartInstance.data.datasets[0].data[index];
                    elevationChartHoverX = elevationChartInstance.scales.x.getPixelForValue(point.x);
                    moveElevationMapMarker(point.lat, point.lon);
                } else {
                    elevationChartHoverX = null;
                }
  
                elevationChartInstance.draw();
            },
            plugins: {
                legend: { display: false },
                tooltip: {
                    mode: 'nearest',
                    intersect: false,
                    callbacks: {
                        title: ctx => `Distance: ${ctx[0].parsed.x.toFixed(1)} km`,
                        label: ctx => `Elevation: ${ctx.parsed.y} m`
                    }
                },
                // annotation: {
                //     annotations: {
                        // lookout: {
                        //     type: 'line',
                        //     xMin: 1.0,
                        //     xMax: 1.0,
                        //     borderColor: 'red',
                        //     borderWidth: 2,
                        //     label: {
                        //         content: 'Lookout',
                        //         enabled: true,
                        //         position: 'start',
                        //         backgroundColor: 'rgba(255,255,255,0.8)',
                        //         color: 'red'
                        //     }
                        // },
                        // rest: {
                        //     type: 'line',
                        //     xMin: 1.5,
                        //     xMax: 1.5,
                        //     borderColor: 'blue',
                        //     borderWidth: 2,
                        //     label: {
                        //         content: 'Rest Area',
                        //         enabled: true,
                        //         position: 'start',
                        //         backgroundColor: 'rgba(255,255,255,0.8)',
                        //         color: 'blue'
                        //     }
                        // }
                //     }
                // }
            },
            scales: {
                x: {
                    type: 'linear',
                    bounds: 'data',
                    title: {
                        display: false,
                        text: 'Distance (km)'
                    },
                    ticks: {
                        // stepSize: 1,
                        callback: value => `${Number(value.toFixed(1))} km`
                    }
                },
                y: {
                    type: 'linear',
                    bounds: 'data',
                    title: {
                        display: true,
                        text: 'Elevation (m)'
                    },
                    ticks: {
                        stepSize: 20
                    }
                }
            }
        },
        plugins: [verticalLinePlugin]

    });
    initElevationChartUI();
}

function initElevationChartUI() {
    //Handle Elevation Map Marker (and touchend events for chart canvas)
    let elevationChartCanvas = document.getElementById('elevationChart');
    
    elevationChartCanvas.addEventListener('mouseout', mouseOutElevationChart);
    elevationChartCanvas.addEventListener('touchend', mouseOutElevationChart);
    elevationChartCanvas.addEventListener('touchcancel', mouseOutElevationChart);
}

function mouseOutElevationChart() {
    elevationChartInstance.setActiveElements([]);
    elevationChartInstance.tooltip.setActiveElements([], { x: 0, y: 0 });
    elevationChartHoverX = null;
    elevationChartInstance.update();
    removeElevationMarker();
}
function removeElevationMarker() {
    if (elevationMapMarker) {
        elevationMapMarker.remove();
        elevationMapMarker = null;
    }
}

function moveElevationMapMarker(lat, lon) {
    // console.log(`📍 Move marker to ${lat}, ${lon}`);
    const lngLat = [lon, lat];
    if (!elevationMapMarker) {
        // 🔹 Create marker once
        // elevationMapMarker = new mapboxgl.Marker({ color: 'red' })
        //     .setLngLat(lngLat)
        //     .addTo(mapInstance);

        // Create a custom marker element for the elevation chart.
        const markerStyle = window.ironman.appConfig.mapMarkers['elevation_chart'];
        const el = document.createElement('div');
        const width = markerStyle.width || 40;
        const height = markerStyle.height || 40;
        const anchor = markerStyle.anchor || 'center';
        el.className = 'ironman-map-marker ironman-map-marker-elevation_chart';
        el.tabIndex = 0;

        // Duplicate logic from above for dynamic markers
        if(markerStyle.type === 'image') {
            el.style.backgroundImage = `url(`+markerStyle.url+`)`;
            el.style.backgroundSize = '100%';
        } else if(markerStyle.type === 'icon') {
            // Alternate to using a background image.
            el.style.backgroundColor = markerStyle.bgColor || 'transparent';
            el.style.backgroundImage = markerStyle.bgImage || 'none';
            el.style.boxShadow = markerStyle.boxShadow || '#ababab 0px 1px 4px 2px';
            el.style.borderRadius = '50%';
            el.style.border = markerStyle.border || '3px solid #fff';
            el.style.fontSize = markerStyle.fontSize || '16px';
            el.style.textAlign = 'center';
            el.style.paddingTop = '2px';
            el.style.color = markerStyle.color || '#fff';
            el.innerHTML = `<i class="`+markerStyle.icon+`"></i>`;
        }
        
        // Set common styles
        el.style.width = `${width}px`;
        el.style.height = `${height}px`;

        elevationMapMarker = new mapboxgl.Marker({ 
            element: el,
            anchor: window.ironman.appConfig.mapMarkers['elevation_chart'].anchor || 'bottom'
        })
            .setLngLat(lngLat)
            .addTo(mapInstance);
    } else {
        // 🔁 Move existing marker
        elevationMapMarker.setLngLat(lngLat);
    }
}

function updateElevationChartData() {
    log('function: updateElevationChartData');

    //Example data:
    // let dummy_data = [
    //     { "x": 0.0, "y": 200, "lat":"46.978", "lon":"-121.816" },
    //     { "x": 0.5, "y": 210, "lat":"46.98", "lon":"-121.86" },
    //     { "x": 1.0, "y": 250, "lat":"45.978", "lon":"-122.816", "place": "Lookout" },
    //     { "x": 1.5, "y": 295, "lat":"45.95", "lon":"-123.816", "place": "Rest Area" }
    // ]
    
    // Get the data from the global variable
    let lineStr = onPathLineString;
    
    if(!lineStr) {
        // return false; // No onPath line string, so no data to update.
        lineStr = []; //empty array to blank the chart.
    }

    //format the data for Chart.js by calculating distance from starting point.
    let data = transformLineStringToChartData(lineStr);

    //trim the points to a maximum of X points, use higher number for longer routes.
    let maxPoints = 200;
    if(lineStr.length > 2500) {
        log('Long route, using 350 points');
        maxPoints = 350;
    }
    data = trimLineStringPoints(data, maxPoints);
    // log(data);


    // Step 1: Update dataset
    elevationChartInstance.data.datasets[0].data = data;

    // Step 2: Extract place-tagged points (e.g., { x: 1.0, y: 250, place: "Lookout" })
    // const annotations = {};
    // data.forEach(point => {
    //     if (point.place) {
    //         annotations[point.place.toLowerCase().replace(/\s/g, '_')] = {
    //             type: 'line',
    //             xMin: point.x,
    //             xMax: point.x,
    //             borderColor: 'gray',
    //             borderWidth: 2,
    //             label: {
    //                 content: point.place,
    //                 enabled: true,
    //                 position: 'start',
    //                 backgroundColor: 'rgba(255,255,255,0.9)',
    //                 color: '#333'
    //             }
    //         };
    //     }
    // });

    // elevationChartInstance.options.plugins.annotation.annotations = annotations;

    // Step 3: Update axis ranges
    // const xs = data.map(p => p.x);
    // const ys = data.map(p => p.y);

    // elevationChartInstance.options.scales.x.min = Math.min(...xs);
    // elevationChartInstance.options.scales.x.max = Math.max(...xs);
    // elevationChartInstance.options.scales.y.min = Math.min(...ys) - 10;
    // elevationChartInstance.options.scales.y.max = Math.max(...ys);

    // Step 4: Redraw
    if(lineStr.length > 0) {
        // delete hardcoded min and max values to allow chart to autoscale.
        delete elevationChartInstance.options.scales.y.min;
        delete elevationChartInstance.options.scales.y.max;
        delete elevationChartInstance.options.scales.x.min;
        delete elevationChartInstance.options.scales.x.max;
    } else {
        // set hardcoded min and max values to avoid errors when no data is present.
        elevationChartInstance.options.scales.y.min = 0;
        elevationChartInstance.options.scales.y.max = 1000;
        elevationChartInstance.options.scales.x.min = 0;
        elevationChartInstance.options.scales.x.max = 1000;
    }
    elevationChartInstance.update();
}

// Function to transform points to chart data, by calculating cumulative distance along lineStr. No longer needs turf.js.
function transformLineStringToChartData(points) {
    const chartData = [];
    let cumulativeDistance = 0;

    for (let i = 0; i < points.length; i++) {
        const [lon, lat, alt] = points[i];
        // const currentPoint = turf.point([lon, lat]);
        const currentPoint = [lon, lat];

        if (i > 0) {
            // const prevPoint = turf.point([points[i - 1][0], points[i - 1][1]]);
            const prevPoint = [points[i - 1][0], points[i - 1][1]];
            // cumulativeDistance += turf.distance(prevPoint, currentPoint, { units: 'kilometers' });
            cumulativeDistance += calculateDistance(prevPoint[1], prevPoint[0], currentPoint[1], currentPoint[0]);
        }

        chartData.push({
            x: Number(cumulativeDistance.toFixed(3)),
            y: alt,
            lat: lat.toFixed(5),
            lon: lon.toFixed(5),
        });
    }

    return chartData;
}

// Calculate distance in km between two points using Haversine formula. Returns distance in km.
function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Radius of the earth in km
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

// Function to trim points to a maximum of 200 points (default)
function trimLineStringPoints(points, MAX_POINTS = 200) {
    const totalPoints = points.length;

    if (totalPoints <= MAX_POINTS) {
        return points; // No trimming needed
    }

    const trimmed = [];
    const step = (totalPoints - 1) / (MAX_POINTS - 1);

    for (let i = 0; i < MAX_POINTS; i++) {
        const index = Math.round(i * step);
        trimmed.push(points[index]);
    }

    return trimmed;
}

function enableElevationChartAndRoutePreview() {
    // Enable elevation chart and route preview buttons.
    // document.getElementById('elevationChartButton').disabled = false;
    // document.getElementById('routePreviewButton').disabled = false;
}

function disableElevationChartAndRoutePreview() {
    // Disable elevation chart and route preview buttons.
    // document.getElementById('elevationChartButton').disabled = true;
    // document.getElementById('routePreviewButton').disabled = true;
}

/**
 * Toggles the map between 2D and 3D.
 */
function toggle3dMap() {
    closeAllPopups();
    if (mapInstance.getPitch() === 0) {
        //Switch to 3D
        doMap3dFly();
    } else {
        //Switch to 2D
        easeTo2d();
    }
}
// window.toggle3dMap = toggle3dMap;

export function easeTo2d() {
    // Trying to stop it from jumping, but it still does.
    // requestAnimationFrame(function () {
        mapInstance.easeTo({pitch: 0, bearing: 0, zoom: 14});
    // });
}

export function setMapTerrain(exaggeration=1) {
    mapInstance.setTerrain({ 'exaggeration': exaggeration });
}
window.setMapTerrain = setMapTerrain;

/**
 * Sets the global map3dSettings based on the routeId, startPlaceId, and endPlaceId.
 * Used the next time the map is switched to 3D.
 * @param {int} routeId 
 * @param {int} startPlaceId 
 * @param {int} endPlaceId - Optional. If not provided, the bearing will be calculated from the start place to the next place on the route.
 */
function setMap3dSettings(routeId=null, startPlaceId=null, endPlaceId=null, direction=null) {
    log('function: setMap3dSettings', routeId, startPlaceId, endPlaceId, direction);
    if(!routeId) {
        // Set to default settings
        map3dSettings.center = null; //Shouldn't matter. Will be compared to current map center when called anyway.
        map3dSettings.bearing = defaultMap3dBearing;
        return;
    }

    const startPlace = getPlaceAlongRoute(routeId, startPlaceId, true);
    const center = [parseFloat(startPlace.lon), parseFloat(startPlace.lat)];
    map3dSettings.center = center;
    
    // Determine End Place
    let endPlace = null;
    if(endPlaceId) {
        endPlace = getPlaceAlongRoute(routeId, endPlaceId, true);
    } else {
        // No end place, get bearing from start place to next place on route.
        let startIndex = getPlaceIndexAlongRoute(routeId, startPlaceId);
        if (startIndex !== false) {
            // StartPlace found on route. Check if there is a next place on the same route.
            const nextPlaceId = getNextPlaceIdAlongRoute(routeId, startPlaceId, direction);
            if(nextPlaceId) {
                endPlace = getPlaceAlongRoute(routeId, nextPlaceId, false);
            } else {
                // No next place along route, (start is last place on route?)
            }
        } else {
            // Place not found along primary route, check child routes 
            const childRouteId = getChildRoutePlaceIsAlong(routeId, startPlaceId);
            if(childRouteId !== false) {
                startIndex = getPlaceIndexAlongRoute(childRouteId, startPlaceId);
                if (startIndex !== false) {
                    // Check if there is a next place on the child route.
                    const nextPlaceId = getNextPlaceIdAlongRoute(childRouteId, startPlaceId, direction);
                    if(nextPlaceId) {
                        endPlace = getPlaceAlongRoute(childRouteId, nextPlaceId, false);
                    } else {
                        // No next place along child route, (start is last place on child route?). There should at least be an intersection....
                    }
                }
            }
        }
        
        if(endPlace) {
            map3dSettings.bearing = utils.getBearing(startPlace.lat, startPlace.lon, endPlace.lat, endPlace.lon);
            log('End place found, setting bearing', endPlace, map3dSettings.bearing);
        } else {
            // No end place. Set bearing to 160? Shouldn't really happen anyway.
            map3dSettings.bearing = 160;
        }
    }
}

function doMap3dFly() {
    const currentMapCenter = mapInstance.getCenter();

    // if center point not set, use current map center
    if(map3dSettings.center === null) {
        map3dSettings.center = [currentMapCenter['lng'], currentMapCenter['lat']];
    }

    // //get number of decimal places in map3dSettings.center
    // const centerSettingDecimals = [
    //     map3dSettings.center[0].toString().split('.')[1].length,
    //     map3dSettings.center[1].toString().split('.')[1].length
    // ];
    
    // var currentMapCenterRounded = [
    //     parseFloat(currentMapCenter['lng'].toFixed(centerSettingDecimals[0])),
    //     parseFloat(currentMapCenter['lat'].toFixed(centerSettingDecimals[1])),
    // ];

    // DEBUGGING: Log the current map center rounded and the map3dSettings center.
    // log('currentMapCenter:', currentMapCenter);
    // log('decimals:', centerSettingDecimals);
    // log('currentMapCenterRounded:', currentMapCenterRounded);
    // log('map3dSettings.center:', map3dSettings.center);

    // Compare to see if we're already at the map3dSettings center.
    // Round both to 3 decimal places and compare.
    const currentMapCenterRounded3 = [
        parseFloat(currentMapCenter['lng'].toFixed(3)),
        parseFloat(currentMapCenter['lat'].toFixed(3)),
    ];
    const map3dSettingsCenterRounded3 = [
        parseFloat(map3dSettings.center[0].toFixed(3)),
        parseFloat(map3dSettings.center[1].toFixed(3)),
    ];
    if((currentMapCenterRounded3[0] === map3dSettingsCenterRounded3[0]) && (currentMapCenterRounded3[1] === map3dSettingsCenterRounded3[1])) {
        //if we're already at the map3dSettings center, just ease to it
        log('Already at map3dSettings center, just ease to it');
        mapInstance.easeTo(map3dSettings);
    } else {
        //changing location, so fly to it
        log('Changing location, so fly to it');
        mapInstance.flyTo(map3dSettings);
    }

    // Now that they're the same number of decimal places, compare them.
    // if(((currentMapCenterRounded[0] === map3dSettings.center[0]) && (currentMapCenterRounded[1] === map3dSettings.center[1])) || (currentMapCenter === map3dSettings.center)) {
    //     //if we're already at the map3dSettings center, just ease to it
    //     log('Already at map3dSettings center, just ease to it');
    //     mapInstance.easeTo(map3dSettings);
    // } else {
    //     //changing location, so fly to it
    //     log('Changing location, so fly to it');
    //     mapInstance.flyTo(map3dSettings);
    // }
}

export function getOnPathLineString() {
    return onPathLineString;
}

// ------------------------------------------------------
// Map Marker Popups --------------------------------------
// ------------------------------------------------------

/**
 * Event listener for map container clicks.
 * @param {MouseEvent} e - The event object.
 */
function onMapContainerClick(e) {
    const markerEl = e.target.closest('.ironman-map-marker');
    if (!markerEl) return;
  
    // Ensure it's within this map
    if (!mapInstance?.getContainer()?.contains(markerEl)) return;
  
    e.stopPropagation();
    handleMapMarkerClick(markerEl);
}

/**
 * Handles the click event for a map marker.
 * @param {HTMLElement} markerEl - The marker element that was clicked.
 */
function handleMapMarkerClick(markerEl) {
    const type = markerEl.getAttribute('data-marker-type');
    const mixedId = markerEl.getAttribute('data-marker-mixed-id');
    const lat = markerEl.getAttribute('data-marker-lat');
    const long = markerEl.getAttribute('data-marker-long');
    if (!type || !mixedId || !lat || !long) {
        log('handleMapMarkerClick: Missing type or mixedId or lat or long', type, mixedId, lat, long);
        return false;
    }
    const routeId = markerEl.getAttribute('data-marker-route-id') || null;

    // Check if the marker is already selected.
    if(selectedMapMarker?.markerEl === markerEl) {
        log('handleMapMarkerClick: Marker already selected, do nothing');
        return true;
    }
  
    // Already validated when the marker was drawn.
  
    // Save selected state to global var
    log('handleMapMarkerClick: Setting selected map marker', markerEl, type, mixedId, lat, long, routeId);
    setSelectedMapMarker(
        { markerEl, type, mixedId, lat, long, routeId }
    );
  
    // Then render UI based on breakpoint
    renderSelectedMarkerUI();
}

/**
 * Handles the breakpoint change event.
 */
function onBreakpointChange() {
    if (!selectedMapMarker) {
        log('onBreakpointChange: No selected map marker');
        return;
    }
  
    if (desktopMediaQuery.matches) {
        // mobile -> desktop
        log('onBreakpointChange: Mobile -> Desktop');
        closeMobilePopup();
        setTimeout(function() {
            // log('onBreakpointChange: Rendering selected marker UI on desktop');
            renderSelectedMarkerUI();
        }, 250);
    } else {
        // desktop -> mobile
        log('onBreakpointChange: Desktop -> Mobile');
        closeDesktopPopup();
        renderSelectedMarkerUI();
    }
}

/**
 * Sets the selected map marker.
 * @param {Object} newState - The new selected map marker.
 */
function setSelectedMapMarker(newState) {
    // newState is either null or { markerEl, type, mixedId, lat, long }
    log('setSelectedMapMarker: Setting selected map marker', newState);

    if (selectedMapMarker?.markerEl && selectedMapMarker.markerEl !== newState?.markerEl) {
        log('setSelectedMapMarker: Deselecting previous marker');
        selectedMapMarker.markerEl.classList.toggle("is-selected", false);
    }
    selectedMapMarker = newState;
  
    if (selectedMapMarker) {
        log('setSelectedMapMarker: Selecting new marker');
        selectedMapMarker.markerEl.classList.toggle("is-selected", true);
    }
}

function getSelectedMapMarker() {
    return selectedMapMarker;
}
window.getSelectedMapMarker = getSelectedMapMarker;

function renderSelectedMarkerUI() {
    log('renderSelectedMarkerUI');
    if (!selectedMapMarker) {
        log('renderSelectedMarkerUI: No selected map marker');
        return false;
    }
  
    const marker = selectedMapMarker;
    let $popup = null;

    // Get data for the marker type
    const placeTypes = new Set(['place', 'origin', 'destination', 'santiago']);
    if (placeTypes.has(marker.type)) {
        // Is PLACE type marker.
        if(marker.routeId) {
            const placeAlongRoute = getPlaceAlongRoute(parseInt(marker.routeId), parseInt(marker.mixedId), true);
            if(!placeAlongRoute) {
                log('renderSelectedMarkerUI: No place along route found for id', marker.mixedId);
                return false;
            }
        }
        const place = getPlaceById(parseInt(marker.mixedId));
        if (!place) {
            log('renderSelectedMarkerUI: No place found for id', marker.mixedId);
            return false;
        }
        log('renderSelectedMarkerUI: Place found', place);
        $popup = getTemplate('placePopupTemplate'); // Returns a jQuery object. Need to call .html() to get the HTML string.
        $popup.find('[data-label="place-name"]').text(place.text).attr('href', '/place/'+place.slug+'/');
        $popup.find('[data-label="place-link"]').attr('href', '/place/'+place.slug+'/');
        $popup.find('[data-label="place-rating"]').text(place.rating_average || 0);
        $popup.find('[data-label="place-rating-count"]').text('(' + (place.rating_count || 0) + ')');

        // Handle post save toggle button.
        const $savePostToggleButton = $popup.find('[data-label="savePostToggleButton"]');
        $savePostToggleButton.attr('data-post-id', place.id);
        if(window.ironman.userData?.userSavedPosts && window.ironman.userData?.userSavedPosts.includes(place.id)) {
            $savePostToggleButton.children('i').removeClass().addClass('ci-heart-fill color-red');
            if($savePostToggleButton.parent().data('bs-toggle') === 'tooltip') {
                const el      = $savePostToggleButton.parent()[0];
                const tooltip = bootstrap.Tooltip.getInstance(el);
                tooltip?.setContent({ '.tooltip-inner': t('removeFromFavoritesTooltipMessage') });
            }
        }

        if(place.featured_image) {
            $popup.find('[data-label="place-image-container-link"]').attr('href', '/place/'+place.slug+'/');
            $popup.find('[data-label="place-image"]').attr('src', place.featured_image);
        } else {
            $popup.find('[data-label="place-image-container-link"]').remove();
        }
        if(place.stamp_image) {
            $popup.find('[data-label="place-stamp"]').attr('src', place.stamp_image).attr('alt', place.text);
        } else {
            $popup.find('[data-label="place-stamp-container"]').remove();
            $popup.find('[data-label="place-stamp"]').remove();
        }
        $popup.find('[data-label="checkin-button"]').attr('data-checkin-post-id', place.id);
        $popup.find('[data-label="use-as-origin-button"]').attr('data-use-as-origin-post-id', place.id);
        // $placePopup.find('[data-label="place-slug"]').text(place.slug);
        $popup.find('[data-label="place-short-description"]').text(place.short_desc || '');
        // $popup.find('[data-label="place-route-icon"]').attr('src', IRONMAN_APP_THEME_ASSET_PATH + 'img/map/way-icon.svg').attr('alt', 'Route Icon');
        // $popup.find('[data-label="place-route-name"]').text(place.routes[0].text);
        const placeTypeName = window.ironman.appConfig?.placeTypes?.places?.children?.[place.type]?.name ?? place.type;
        $popup.find('[data-label="place-type"]').children('i').addClass(window.ironman.appConfig?.placeTypes?.places?.children?.[place.type]?.icon ?? '');
        $popup.find('[data-label="place-type"]').children('span').text(placeTypeName);
        // $popup.find('[data-label="place-rating"]').text(place.rating);
        $popup.find('[data-label="place-route-link-container"]').empty();
        if(place.routes && place.routes.length > 0) {
            place.routes.forEach(function(route) {
                let routeData = getRouteById(parseInt(route));
                let childRouteName = null;
                log('renderSelectedMarkerUI: Route data', routeData);
                if(routeData) {
                    if(utils.hasKey(routeData, 'parent')) {
                        // This is a child route. Get the parent route data.
                        // But first, save the child route name to use later.
                        childRouteName = childRouteNameLookup(routeData.slug);
                        routeData = getRouteById(parseInt(routeData.parent));
                        log('renderSelectedMarkerUI: Parent route data', routeData);
                    }
                    const routeLink = getTemplate('placeRouteLinkItemTemplate').clone();
                    const routeName = window.ironman.appConfig.routeTaxonomy[routeData.slug]?.name ?? routeData.text;
                    routeLink.find('[data-label="place-route-icon"]').attr('src', window.ironman.appConfig.primaryRoutes[routeData.id].icon).attr('alt', routeName);
                    routeLink.find('[data-label="place-route-name"]').text(routeName);
                    if(childRouteName) {
                        routeLink.find('[data-label="child-route-name"]').text('('+childRouteName+')').removeClass('d-none');
                    }
                    routeLink.find('[data-label="place-route-link"]').attr('href', '/camino/'+routeData.slug+'/');
                    $popup.find('[data-label="place-route-link-container"]').append(routeLink);
                }
            });
        }
        // Show the service icons based on the place services.
        const $serviceContainer = $popup.find('[data-label="place-services-container"]');
        if($serviceContainer.length > 0) {
            // Get the filtered service categories list.
            const filteredServices = filterMasterServicesList(place.services || []);
            const serviceCatsArray = Object.keys(filteredServices);
            serviceCatsArray.forEach(function(serviceCat) {
                $serviceContainer.find('[data-service-slug="'+serviceCat+'"]').removeClass('d-none');
            });
        }
        openPopup(marker.type, marker.mixedId, marker.lat, marker.long, $popup);
        return true;
    } else if(marker.type === 'listing') {
        const state = getStateFromUrl();
        if(!state.route) {
            log('renderSelectedMarkerUI: No route found in state');
            return false;
        }
        // Get the listings data from the fetchedAPIData cache variable
        // MUST HAVE ALREADY BEEN FETCHED, SINCE THE POPUP CAN ONLY BE OPENED BY CLICKING A MARKER ON THE MAP.
        getFetchedAPIData('listings', state.route).then(function(listingsData) {
            // log('listings data decrypted successfully:', listingsData);
            const listing = listingsData.listings[marker.mixedId];
            log('renderSelectedMarkerUI: Listing found', listing);
            if(listing) {
                $popup = getTemplate('listingPopupTemplate'); // Returns a jQuery object. Need to call .html() to get the HTML string.
                $popup.find('[data-label="listing-title"]').text(listing.title).attr('href', listing.link);
                $popup.find('[data-label="listing-link"]').attr('href', listing.link);
                $popup.find('[data-label="listing-media-gallery-overlay"]').attr('href', listing.link);
                $popup.find('[data-label="listing-rating"]').text(listing.rating_average || 0);
                $popup.find('[data-label="listing-rating-count"]').text('(' + (listing.rating_count || 0) + ')');

                //If no reviews yet, hide the rating and rating count.
                if(!listing.rating_count || listing.rating_count === 0) {
                    $popup.find('[data-label="listing-rating-container"]').addClass('d-none');
                }
                // if(listing.short_desc) {
                //     $popup.find('[data-label="listing-short-description"]').text(listing.short_desc);
                // } else {
                //     $popup.find('[data-label="listing-short-description"]').remove();
                // }

                // Handle post save toggle button.
                const $savePostToggleButton = $popup.find('[data-label="savePostToggleButton"]');
                $savePostToggleButton.attr('data-post-id', listing.id);
                if(window.ironman.userData?.userSavedPosts && window.ironman.userData?.userSavedPosts.includes(listing.id)) {
                    $savePostToggleButton.children('i').removeClass().addClass('ci-heart-fill color-red');
                    if($savePostToggleButton.parent().data('bs-toggle') === 'tooltip') {
                        const el      = $savePostToggleButton.parent()[0];
                        const tooltip = bootstrap.Tooltip.getInstance(el);
                        tooltip?.setContent({ '.tooltip-inner': t('removeFromFavoritesTooltipMessage') });
                    }
                }

                // Add the services to the popup.
                if(listing.services && listing.services.length > 0) {
                    const filteredServices = filterMasterServicesList(listing.services || []);
                    const $servicesContainer = $popup.find('[data-label="listing-services-container"]');
                    const serviceItemsTemplate = getTemplate('listingServiceItemTemplate');
                    const serviceItems = [];
                    Object.entries(filteredServices).forEach(function([serviceCat, service]) {
                        Object.entries(service.children).forEach(function([serviceSlug, service]) {
                            const $serviceItem = serviceItemsTemplate.clone();
                            $serviceItem.find('[data-label="listing-service-icon"]').addClass(service.icon);
                            $serviceItem.find('[data-label="listing-service-name"]').text(service.name);
                            serviceItems.push($serviceItem);
                        });
                    });
                    $servicesContainer.append(serviceItems);
                }

                // Add the media gallery items to the popup.
                if(listing.media_gallery && listing.media_gallery.length > 0) {
                    const $mediaGalleryContainer = $popup.find('[data-label="listing-media-gallery-container"]');
                    const mediaItems = [];
                    listing.media_gallery.forEach(function(media) {
                        const $mediaItem = getTemplate('listingMediaGalleryItemTemplate').clone();
                        $mediaItem.find('[data-label="listing-media-gallery-image"]').attr('src', media.media_url_thumbnail).attr('alt', media.media_caption);
                        mediaItems.push($mediaItem);
                    });
                    $mediaGalleryContainer.append(mediaItems);
                }

                // Handle the hotel-specific features
                if(listing.booking_url && listing.booking_url.length > 0) {
                    $popup.find('[data-label="listing-booking-com-link"]').attr('href', listing.booking_url);
                    $popup.find('[data-label="listing-booking-com-button"]').removeClass('d-none');
                }
                if(listing.hotels_com_url && listing.hotels_com_url.length > 0) {
                    $popup.find('[data-label="listing-hotels-com-link"]').attr('href', listing.hotels_com_url);
                    $popup.find('[data-label="listing-hotels-com-button"]').removeClass('d-none');
                }

                // Handle Room Types labels
                if(listing.hotel_room_types && listing.hotel_room_types.length > 0) {
                    if(listing.hotel_room_types.includes('private-room')) {
                        $popup.find('[data-label="listing-has-private-rooms"]').removeClass('d-none');
                    }
                    if(listing.hotel_room_types.includes('shared-room')) {
                        $popup.find('[data-label="listing-has-shared-rooms"]').removeClass('d-none');
                    }
                }

                // Handle Hotel Amenities labels
                if(listing.hotel_amenities && listing.hotel_amenities.length > 0) {
                    const amenityItems = [];
                    const amenityItemsTemplate = getTemplate('listingAmenityItemTemplate');
                    listing.hotel_amenities.forEach(function(amenity) {
                        const $amenityItem = amenityItemsTemplate.clone();
                        const amenityData = window.ironman.appConfig.hotelAmenities[amenity];
                        $amenityItem.find('[data-label="listing-amenity-icon"]').addClass(amenityData.icon);
                        $amenityItem.find('[data-label="listing-amenity-name"]').text(amenityData.name);
                        amenityItems.push($amenityItem);
                    });
                    if(amenityItems.length > 0) {
                        $popup.find('[data-label="listing-amenity-items-container"]').append(amenityItems);
                    }
                }

                openPopup(marker.type, marker.mixedId, marker.lat, marker.long, $popup);

                // Initialize the Swiper instance.
                // Swiper initialized by Fenix open function.
                // initSwipersIn($popup.get(0));
                return true;
            } else {
                log('renderSelectedMarkerUI: No listing found for id', marker.mixedId);
                return false;
            }
        }, function(error) {
            log('Error fetching listings data:', error);
            // Handle error, e.g. show an alert or fallback UI
            return false;
        });
    } else if(marker.type === 'friend') {
        // Get the friends data from the fetchedAPIData cache variable
        // MUST HAVE ALREADY BEEN FETCHED, SINCE THE POPUP CAN ONLY BE OPENED BY CLICKING A MARKER ON THE MAP.
        getFetchedAPIData('friends').then(function(friendsData) {
            // log('friends data decrypted successfully:', friendsData);
            const friend = friendsData.friends[marker.mixedId];
            if(friend) {
                $popup = getTemplate('mapFriendPopupTemplate'); // Returns a jQuery object. Need to call .html() to get the HTML string.
                $popup.find('[data-label="friend-name"]').text(friend.name).attr('href', friend.link);
                $popup.find('[data-label="friend-link"]').attr('href', friend.profileLink);
                $popup.find('[data-label="friend-image"]').attr('src', friend.profilePictureURL).attr('alt', friend.name);
                $popup.find('[data-label="friend-sub-header"]').text(friend.checkinIsEnRouteToPlace ? t('enRouteToPlace')+getPlaceById(friend.checkinPlaceId).text : t('linkedPlace')+' ('+friend.hoursSinceCheckin+' '+t('hoursAgo')+')');
                const hoursSinceCheckin = round0(friend.hoursSinceCheckin);
                const hoursSinceCheckinText = hoursSinceCheckin === 0 ? t('lessThanAnHourAgo') : hoursSinceCheckin > 1 ? hoursSinceCheckin+' '+t('hoursAgo') : t('oneHourAgo');
                $popup.find('[data-label="friend-checked-in-date"]').text(hoursSinceCheckinText);
                
                openPopup(marker.type, marker.mixedId, marker.lat, marker.long, $popup);
                return true;
            } else {
                log('renderSelectedMarkerUI: No friend found for id', marker.mixedId);
                return false;
            }
        }, function(error) {
            log('Error fetching friends data:', error);
            // Handle error, e.g. show an alert or fallback UI
            return false;
        });
    } else {
        log('renderSelectedMarkerUI: Unknown marker type', marker.type);
        return false;
    }
}

/**
 * Opens the popup based on the breakpoint.
 * NOTE: Separated out from render function to allow for working either sync or async.
 * @param {string} type - The type of marker.
 * @param {int} mixedId - The mixed id of the marker.
 * @param {float} lat - The latitude of the marker.
 * @param {float} long - The longitude of the marker.
 * @param {jQuery} $popup - The jQuery object containing the popup content.
 */
function openPopup(type, mixedId, lat, long, $popup) {
    if (desktopMediaQuery.matches) {
        openDesktopPopup(type, mixedId, lat, long, $popup.html());
    } else {
        openMobilePopup(type, mixedId, lat, long, $popup);
    }
}

function openDesktopPopup(type, mixedId, lat, long, html) {
    closeDesktopPopup(); // Close the current desktop popup if one is open.

    log('openDesktopPopup: Opening desktop popup', type, mixedId, lat, long);

    openMapMarkerPopup = new mapboxgl.Popup({
        maxWidth: 'none',
    //   offset: 16,
      closeButton: false,
      closeOnClick: true
    })
      .setLngLat([long, lat])
      .setHTML(html);

    openMapMarkerPopup.once("open", () => {
        log('openDesktopPopup: Map marker popup opened');
        initSwipersIn(openMapMarkerPopup.getElement());
    });

    openMapMarkerPopup.once("close", () => {
        log('openDesktopPopup: Map marker popup closed');
        if(desktopMediaQuery.matches) {
            // If desktop map marker closes and the screen is still desktop, clear the selected map marker. Otherwise it's from a breakpoint change.
            clearSelectedMapMarker();
        }
        if(getMapPopupSwiper_swiperInstance()) {
            log('openDesktopPopup: Destroying Swiper instance');
            getMapPopupSwiper_swiperInstance().destroy();
            nullMapPopupSwiper_swiperInstance();
        }
    });

    openMapMarkerPopup.addTo(mapInstance);
}

async function openMobilePopup(type, mixedId, lat, long, $content) {
    closeDesktopPopup();
  
    // Check the backdrop state of the Fenix modal and re-init if it's with a backdrop.
    await setFenixBackdrop(false);

    // Set Fenix Modal Content with placeholder and open it. (If not already open)
    if(getFenixModalIsOpen() === false) {
        setFenixModalContent(null, getTemplate('loadingPlaceholder'), null, null, 'short', 'mapMarkerPopup', true);
        await openFenixModal(false);
        await nextFrame(); //wait for the next frame to ensure the modal is open before continuing.
        // await delay(1000); //wait N milliseconds for testing.
    }

    // Set Fenix Modal Content with the actual content.
    setFenixModalContent(true, $content, true, true, 'short', 'mapMarkerPopup', true);

    // Add a callback to the modal to remove the selected global when the modal is hidden (if the screen is still mobile).
    const fenixEl = getFenixModalElement();
    fenixEl.addEventListener('hidden.bs.offcanvas', onCloseMobilePopup);
  
    // Optional: keep marker visible above the sheet
    mapInstance.easeTo({
      center: [long, lat],
      padding: { top: 40, left: 40, right: 40, bottom: 360 }
    });
}

function closeDesktopPopup() {
    if (openMapMarkerPopup) {
        const popup = openMapMarkerPopup;
        openMapMarkerPopup = null; // clear the global var
        popup.remove(); // remove the popup from the map
    }
}

function closeMobilePopup() {
    closeFenixModal();
}
  
function clearSelectedMapMarker() {
    if (!selectedMapMarker) return;
    log('clearSelectedMapMarker: Clearing selected map marker');

    // Remove is_selected class from the selected marker element
    selectedMapMarker.markerEl.classList.toggle("is-selected", false);

    // Clear the selected map marker global var
    selectedMapMarker = null;

    // Remove the callback from the Fenix modal.
    const fenixEl = getFenixModalElement();
    fenixEl.removeEventListener('hidden.bs.offcanvas', onCloseMobilePopup);
}
  
function closeAllPopups() {
    closeDesktopPopup();
    closeFenixModal();
    clearSelectedMapMarker();
}

function onCloseMobilePopup() {
    if(desktopMediaQuery.matches) {
        return;
    }
    clearSelectedMapMarker();
}

async function toggleMapStyleSelectModal() {
    log('toggleMapStyleSelectModal');
    // search DOM for .mapStyleSelectModalContainer
    const mapStyleSelectModalContainer = $('.mapStyleSelectModalContainer');
    if(mapStyleSelectModalContainer.length > 0) {
        closeFenixModal();
        return false;
    } else {
        showMapStyleSelectModal();
    }
}
async function showMapStyleSelectModal() {
    log('showMapStyleSelectModal');

    // Make sure the Fenix modal has a backdrop.
    await setFenixBackdrop(false);

    // Update the Fenix modal with the Route Select modal content.
    const modalTitle = t('changeMapStyleModal_title');
    const modalFooter = null;
    const modalDesktopSize = 'xs';
    const modalMobileHeight = 'mini';
    const modalDataPurpose = 'mapStyleSelect'; // To ID this modal content for JS/CSS selectors. This is used to identify the modal content for JS/CSS selectors.
    
    // Load animated bootstrap placeholder for loading.
    setFenixModalContent(modalTitle, getTemplate('loadingPlaceholder'), modalFooter, modalDesktopSize, modalMobileHeight, modalDataPurpose);
    await openFenixModal();
    await nextFrame(); //wait for the next frame to ensure the modal is open before continuing.
    
    let modalBody = getTemplate('mapStyleSelectModalTemplate');
    // Start customizing the modal body...

    // // Get currently selected style if any.
    // const selectedStyle = getMapStyle(startingMapStyle);
    // log('selectedStyle', selectedStyle);
    
    // //Get state from URL
    // const state = getStateFromUrl();
    // log('state', state);



    // Build the map style swiper items
    const swiperItemTemplate = getTemplate('imageSelectSwiperItemTemplate');
    const mapStyles = window.ironman.appConfig.mapStyles;
    const state = getStateFromUrl();
    let defaultChecked = false;

    if(Object.keys(mapStyles).length > 0) {
        const mapSwiperItems = [];
        Object.entries(mapStyles).forEach(([key, style]) => {
            log('showMapStyleSelectModal: Adding swiper item for map style', key, style);
            const item = swiperItemTemplate.clone();
            item.find('img').attr('src', style.thumbnail_url);
            
            if(state.style && state.style === key) {
                item.find('input').prop('checked', true);
                defaultChecked = true;
            }

            // Check user permission to use this map style
            if(window.ironman.userData.permissions.allowedMapStyles.includes(key)) {
                item.find('input').prop('disabled', false);
                item.find('input').attr('name', 'map-style');
                item.find('input').attr('value', key);
                item.find('label').attr('data-action', 'changeMapStyle');
            } else {
                item.find('input').prop('disabled', true);
                item.find('label').addClass('upgrade-required');
            }

            mapSwiperItems.push(item);
        });

        if(!defaultChecked) {
            //Check the first item by default
            mapSwiperItems[0].find('input').prop('checked', true);
        }
        
        // Add the swiper items to the modal body
        modalBody.find('.swiper-wrapper').empty().append(mapSwiperItems);
    } else {
        modalBody.find('.swiper-wrapper').empty().html('<p>'+t('noMapStylesAvailable')+'</p>');
    }

    
    // End customizing the modal body...

    // Set the modal content. True means don't change it - for use when the modal is already open.
    setFenixModalContent(true, modalBody, true, true, true, true);
}

function getSelectedMarkerFilterButtons() {
    return $('input[name="marker-filters"]:checked')
        .map(function() {
            return this.value;
        }).get() ?? [];
}

function updateMarkerFilterButtonsUI(selected = []) {
    const $markerFiltersContainer = $('[data-label="map-toolbar-marker-filters-container"]');
    if($markerFiltersContainer.length > 0) {
        $markerFiltersContainer.find('input[name="marker-filters"]').each(function() {
            const buttonValue = $(this).val();
            if(selected.includes(buttonValue)) {
                $(this).prop('checked', true);
            } else {
                $(this).prop('checked', false);
            }
        });
    };
}
function showHideListViewButton(selected = []) {
    // Check if the showServiceListviewButton should be shown.

    // TODO: Listview.
    // if(selected.length > 0) {
    //     $('[data-label="show-service-listview-button"]').removeClass('d-none');
    // } else {
    //     $('[data-label="show-service-listview-button"]').addClass('d-none');
    // }
}